import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { LocaleProvider, useLocale } from "@app/ui";

/**
 * Panel localization (TR/EN). The UI language drives the @app/ui LocaleProvider
 * (number/date formatting) AND the string dictionary below, so a single toggle
 * switches both. The choice persists in localStorage.
 */

export type Lang = "en" | "tr";

const LANG_STORAGE_KEY = "dpp.lang";
const LOCALE_BY_LANG: Record<Lang, string> = { en: "en-US", tr: "tr-TR" };

const en = {
  "nav.section.overview": "Overview",
  "nav.section.catalog": "Products & Passports",
  "nav.section.admin": "Administration",
  "nav.section.account": "Account",
  "nav.section.platform": "Platform",
  "platform.showDemo": "Show demo data",
  "platform.demoTag": "Demo",
  "platform.subtitleNoDemo":
    "Real customers only — {orgs} demo organizations and {users} demo accounts are hidden.",
  "platform.subtitleWithDemo": "Including demo organizations and demo accounts.",
  "nav.platformOverview": "Overview",
  "nav.allProducts": "All products",
  "nav.allPassports": "All passports",
  "platform.title": "Platform overview",
  "platform.subtitle": "Every organization, user and passport on this installation.",
  "platform.note.activeOrgs": "{n} active",
  "platform.note.disabledUsers": "{n} disabled",
  "platform.note.views30d": "{n} passport views in 30 days",
  "platform.note.published": "{n} published",
  "platform.note.draft": "{n} draft",
  "platform.attention": "Needs attention",
  "platform.allGood": "Nothing needs attention right now.",
  "platform.views": "Passport views — last 30 days",
  "platform.noViews": "No passport views in the last 30 days.",
  "platform.recentOrgs": "Newest organizations",
  "platform.seeAll": "See all",
  "platform.att.delinquent": "Organizations with overdue invoices",
  "platform.att.viewLimit": "Organizations over their view allowance",
  "platform.att.suspended": "Suspended organizations",
  "platform.att.orgsWithoutMembers": "Organizations without members",
  "platform.att.usersWithoutOrg": "Users without an organization",
  "platform.att.unverified": "Users with unverified email",
  "platform.att.disabledUsers": "Disabled users",
  "platform.allOrgs": "All organizations",
  "platform.allProducts": "All products",
  "platform.searchProducts": "Search name, SKU or GTIN…",
  "platform.noProducts": "No products match",
  "platform.product": "Product",
  "platform.allPassports": "All passports",
  "platform.searchPassports": "Search product, SKU or public slug…",
  "platform.noPassports": "No passports match",
  "platform.version": "Version",
  "platform.publicPage": "Public page",
  "platform.viaPlatformBanner":
    "You are working in {org} as the platform administrator (not a member). Changes are recorded under your name.",
  "platform.backToConsole": "Back to the console",
  "adminOrgs.planChanged": "{name}: plan updated",
  "adminOrgs.flag.delinquent": "Overdue invoice",
  "adminOrgs.flag.viewLimit": "View allowance used up",
  "adminOrgs.flag.noMembers": "No members",
  "adminUsers.search": "Search email or name…",
  "adminUsers.anyOrg": "Any organization",
  "adminUsers.withoutOrg": "Without organization",
  "adminUsers.user": "User",
  "adminUsers.locked": "locked",
  "adminUsers.lastActive": "Last active",
  "adminUsers.passwordSet": "Password updated; the user was signed out everywhere",
  "adminUsers.unlock": "Unlock",
  "adminUsers.unlocked": "Lockout cleared",
  "adminUsers.setPassword": "Set password",
  "adminUsers.passwordSetHandOver":
    "New password — share it securely; it is not shown again. The user was emailed that their password changed.",
  "adminUsers.setPasswordHelp":
    "For support cases (lost password, reset email not arriving). The user is signed out everywhere and notified by email.",
  "adminUsers.newPassword": "New password",
  "nav.adminOrgs": "Organizations",
  "adminOrgs.title": "All organizations",
  "adminOrgs.create": "New organization",
  "adminOrgs.summary":
    "{orgs} organizations · {products} products · {published} published of {passports} passports",
  "adminOrgs.search": "Search name or slug…",
  "adminOrgs.allStatuses": "All statuses",
  "adminOrgs.empty": "No organizations match",
  "adminOrgs.name": "Organization",
  "adminOrgs.members": "Members",
  "adminOrgs.plan.free": "Free",
  "adminOrgs.plan.unlimited": "Unlimited",
  "adminOrgs.plan.none": "Not billed",
  "adminOrgs.manage": "Members & settings",
  "adminOrgs.status.active": "Active",
  "adminOrgs.status.suspended": "Suspended",
  "adminOrgs.status.wound_down": "Wound down",
  "adminOrgs.statusConfirmTitle": "Change organization status",
  "adminOrgs.statusConfirmBody": "Set {name} to: {status}?",
  "adminOrgs.statusReason": "Reason (kept in the audit log)",
  "adminOrgs.statusChanged": "{name}: status updated",
  "adminUsers.create": "New user",
  "adminUsers.created": "User created.",
  "adminUsers.handOver":
    "Share these credentials securely; the password is not shown again. The user can change it from Profile.",
  "adminUsers.displayName": "Name",
  "adminUsers.password": "Initial password",
  "adminUsers.generate": "Generate",
  "adminUsers.platformRole": "Platform role",
  "adminUsers.platformRoleHelp":
    "Platform roles act across all organizations (e.g. admin, auditor). Customers usually need none — give them an organization role instead.",
  "adminUsers.noPlatformRole": "None (organization roles only)",
  "adminUsers.firstOrg": "Add to organization",
  "adminUsers.noOrg": "Don't add yet",
  "adminUsers.orgAddFailed": "User created, but adding to the organization failed",
  "adminUsers.emailVerified": "email verified",
  "adminUsers.emailUnverified": "email not verified",
  "adminUsers.revokeSessions": "Sign out everywhere",
  "adminUsers.sessionsRevoked": "All sessions revoked",
  "adminUsers.enabled": "User enabled",
  "adminUsers.disabledMsg": "User disabled",
  "adminUsers.platformRoles": "Platform roles",
  "adminUsers.pickRole": "Pick a role…",
  "adminUsers.assignRole": "Assign",
  "adminUsers.roleAssigned": "Role {role} assigned",
  "adminUsers.roleRevoked": "Role {role} removed",
  "adminUsers.orgs": "Organization memberships",
  "adminUsers.noOrgs": "Not a member of any organization",
  "adminUsers.pickOrg": "Pick an organization…",
  "adminUsers.addToOrg": "Add",
  "adminUsers.orgAdded": "Added to {org}",
  "adminUsers.orgRemoved": "Removed from {org}",
  "adminUsers.orgRoleChanged": "Role in {org} updated",
  "nav.dashboard": "Dashboard",
  "nav.users": "Users",
  "nav.audit": "Audit",
  "nav.license": "License",
  "nav.uiKit": "UI Kit",
  "nav.products": "Products",
  "nav.passports": "Passports",
  "nav.import": "Import",
  "nav.org": "Organization",
  "nav.profile": "Profile",

  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.create": "Create",
  "common.actions": "Actions",
  "common.status": "Status",
  "common.createdAt": "Created",
  "common.updatedAt": "Updated",
  "common.loading": "Loading…",
  "common.view": "View",
  "common.viewList": "List view",
  "common.viewGrid": "Card view",
  "common.totalCount": "{count} total",
  "common.noSearchResults": "No records match your filters",
  "common.clearFilters": "Clear filters",
  "common.yes": "Yes",
  "common.no": "No",
  "common.remove": "Remove",
  "common.moveUp": "Move up",
  "common.moveDown": "Move down",
  "common.archive": "Archive",
  "common.signOut": "Sign out",
  "common.openNav": "Open navigation",
  "common.toggleTheme": "Toggle theme",
  "common.menu": "Menu",
  "common.language": "Language",
  "common.email": "Email",
  "common.roles": "Roles",
  "common.mfa": "MFA",
  "common.ip": "IP",

  "error.loadFailed": "Failed to load data",
  "error.saveFailed": "Failed to save",
  "error.actionFailed": "The action failed",

  "demo.banner":
    "Demo account — you can explore every form, but changes are not saved. Public passport pages stay live.",
  "demo.writeBlocked": "This is a demo account — changes can't be saved.",

  "org.switcher": "Organization",
  "org.search": "Search organization…",
  "org.create": "Create organization",
  "org.createTitle": "New organization",
  "org.name": "Name",
  "org.slug": "Slug",
  "org.slugHelp": "Lowercase letters, digits and hyphens (used in URLs).",
  "org.gs1Prefix": "GS1 company prefix",
  "org.gs1PrefixHelp": "4–12 digits, as assigned by GS1 (optional).",
  "org.plan": "Plan",
  "org.addSelfOwner": "Add me as the first owner",
  "org.asSubOrg": "Create as a sub-organization of",
  "billingPlan.free": "Free (1000 views/yr, 10 products)",
  "billingPlan.unlimited": "Unlimited ($1000/yr, unlimited views and products)",
  "billingPlan.none": "Not billed",
  "org.created": "Organization created",
  "org.empty": "You are not a member of any organization yet.",
  "org.emptyHint": "Ask a platform administrator to add you to an organization.",
  "org.emptyTitle": "Create your first organization",
  "org.emptyCtaHint":
    "Set up an organization to start managing products and their digital product passports.",

  "products.title": "Products",
  "products.new": "New product",
  "products.createTitle": "New product",
  "products.created": "Product created",
  "products.searchPlaceholder": "Search SKU or name…",
  "products.empty": "No products yet",
  "products.sku": "SKU",
  "products.name": "Name",
  "products.gtin": "GTIN",
  "products.gtinHelp": "8, 12, 13 or 14 digits (optional).",
  "products.commodityCode": "Commodity code (CN)",
  "products.commodityCodeHelp": "8-10 digits — EU customs CN/TARIC code, sent to the DPP Registry.",
  "products.gtip": "GTİP (Turkish customs)",
  "products.gtipHelp":
    "12 digits — Turkish customs tariff position; its first 8 digits should match the CN code.",
  "products.gtipMismatch": "The first 8 digits of the GTİP do not match the CN commodity code.",
  "products.schema": "Passport schema",

  "productStatus.draft": "draft",
  "productStatus.active": "active",
  "productStatus.archived": "archived",

  "product.notFound": "Product not found",
  "product.unknownSchema": "Unknown passport schema — this product cannot be edited here.",
  "product.compliance": "Compliance",
  "product.complete": "All required fields are filled.",
  "product.missingRequired": "Missing required fields",
  "product.saved": "Product saved",
  "product.addRow": "Add row",
  "product.data": "Product data",
  "product.missingShort": "missing",
  "product.noRows": "No rows yet — add the first one.",
  "product.editedCount": "field(s) changed",
  "product.discard": "Discard",

  "common.edit": "Edit",
  "nav.facilities": "Facilities",
  "nav.disclosures": "Unsold-goods disclosure",

  "facilities.title": "Facilities & Certifications",
  "facilities.facilities": "Facilities",
  "facilities.new": "New facility",
  "facilities.edit": "Edit facility",
  "facilities.saved": "Facility saved",
  "facilities.deleted": "Facility removed",
  "facilities.empty": "No facilities yet — add your plants and suppliers' sites.",
  "facilities.name": "Name",
  "facilities.location": "Location",
  "facilities.country": "Country",
  "facilities.countryPlaceholder": "Search country…",
  "facilities.city": "City",
  "facilities.employees": "Employees",
  "facilities.renewable": "Renewable energy",
  "facilities.lat": "Latitude",
  "facilities.lng": "Longitude",
  "facilities.waterRecycling": "Water recycling",
  "facilities.wastewaterTreatment": "Wastewater treatment",
  "facilities.practices": "Manufacturing practices",
  "facilities.certifications": "Certifications",
  "facilities.mapTitle": "Facility locations",
  "facilities.identifier": "Identifier",
  "facilities.identifierScheme": "Identifier scheme",
  "facilities.identifierNone": "None (platform URN)",
  "facilities.identifierInternal": "Internal code",
  "facilities.identifierValue": "Identifier value",
  "facilities.identifierHelp":
    "GLN: 13 digits with a valid check digit. Without one, the passport publishes a stable platform URN for the site.",

  "map.search": "Search",
  "map.searchPlaceholder": "Search for an address or place…",
  "map.noResult": "No location found for that search.",
  "map.unavailable": "Map is not configured — enter the coordinates manually.",
  "map.pickHint": "Click the map or drag the marker to set the exact location.",

  "certs.title": "Certifications",
  "certs.new": "New certification",
  "certs.edit": "Edit certification",
  "certs.saved": "Certification saved",
  "certs.deleted": "Certification removed",
  "certs.empty": "No certifications yet — record GOTS, OEKO-TEX, ISO and similar certificates.",
  "certs.scheme": "Scheme",
  "certs.no": "Certificate no",
  "certs.issuer": "Issuer",
  "certs.validFrom": "Valid from",
  "certs.validUntil": "Valid until",
  "certs.evidenceUrl": "Evidence link",
  "certs.noExpiry": "no expiry",
  "certs.image": "Certificate image",
  "certs.imageUpload": "Upload image",
  "certs.imageReplace": "Replace image",
  "certs.imageRemove": "Remove image",
  "certs.imageHint": "JPEG, PNG or WebP, up to 5 MB. Shown on the public passport next to the certificate.",
  "certs.imagePending": "The image is uploaded once the certificate is saved.",
  "certs.imageUploaded": "Certificate image uploaded",
  "certs.imageDeleted": "Certificate image removed",
  "certs.imageFailed": "The certificate was saved but its image could not be uploaded.",

  "chain.title": "Supply chain",
  "chain.hint":
    "Order the stages from raw material to delivery. Restricted stages are visible only to access-grant holders.",
  "chain.empty": "No stages yet.",
  "chain.saved": "Supply chain saved",
  "chain.save": "Save supply chain",
  "chain.addStage": "Add stage",
  "chain.transport": "Transport method",
  "chain.notes": "Notes",
  "chain.noFacility": "No facility",
  "chain.productCerts": "Product certifications",
  "chain.stage": "Stage",
  "chain.facility": "Facility",
  "chain.stagesTitle": "Production stages",
  "chain.stageCount": "{n} stage(s)",
  "chain.mapTitle": "Production route",
  "chain.certsHint": "Certifications that apply to the finished product.",
  "chain.partnersHint": "Take-back and circularity partners offered for this product.",
  "chain.unsaved": "Review the chain, then save your changes.",

  "comps.title": "Components",
  "comps.hint": "Component-level traceability: list the parts the product is made of, with their source.",
  "comps.empty": "No components yet.",
  "comps.saved": "Components saved",
  "comps.save": "Save components",
  "comps.add": "Add component",
  "comps.addChild": "Add sub-part",
  "comps.name": "Component name",
  "comps.type": "Type",
  "comps.weight": "Weight",
  "comps.image": "Image",
  "comps.imageSet": "Image ✓",
  "comps.materialNote": "Material / note",
  "comps.separable": "Separable part",
  "comps.facility": "Facility",
  "comps.replaceImage": "Replace",
  "comps.removeImage": "Remove image",
  "comps.rootTitle": "Bill of materials",
  "comps.compCount": "{n} part(s)",

  "dashboard.expiringCerts": "Certifications expiring within 90 days:",
  "dashboard.expiringCertsLink": "manage certifications",

  "nav.reviews": "Reviews",
  "lca.title": "LCA",
  "lca.hint":
    "Life Cycle Assessment result with its methodology — shown on the public passport with source and dataset declarations.",
  "lca.saved": "LCA result saved",
  "lca.deleted": "LCA result removed",
  "lca.delete": "Remove LCA",
  "lca.source": "Source",
  "lca.sourceManual": "Manual declaration",
  "lca.sourceEpd": "EPD reference",
  "lca.sourceClca": "clca.app engine",
  "lca.externalRef": "External reference",
  "lca.method": "Method",
  "lca.functionalUnit": "Functional unit",
  "lca.systemBoundary": "System boundary",
  "lca.dataset": "Dataset",
  "lca.calculatedAt": "Calculated at",
  "lca.verifiedBy": "Verified by",
  "lca.indicators": "Indicators",
  "lca.addIndicator": "Add indicator",
  "lca.indicator": "Indicator",
  "lca.value": "Value",
  "lca.unit": "Unit",
  "lca.module": "Module",

  "lca.methodology": "Method & source",
  "lca.methodologyHint":
    "These declarations are published next to the numbers — a result without its method, boundary and dataset is not verifiable.",
  "lca.externalRefPh": "EPD registration no. or URL",
  "lca.externalRefHelp": "Registry number of the EPD / verification report, or its public URL.",
  "lca.methodPh": "e.g. EN 15804+A2",
  "lca.functionalUnitPh": "e.g. 1 piece",
  "lca.systemBoundaryPh": "e.g. cradle to gate",
  "lca.datasetPh": "e.g. ecoinvent 3.11",
  "lca.verifiedByPh": "Verifier or programme operator",
  "lca.indicatorsHint":
    "One row per declared indicator. Picking a code from the catalog fills in its EN 15804 unit.",
  "lca.indicatorPh": "Search or type a code",
  "lca.indicatorPick": "Add indicator…",
  "lca.indicatorCustom": 'Add "{q}" as a custom code',
  "lca.addCoreSet": "EN 15804+A2 core set",
  "lca.addHeadlineSet": "Carbon · energy · water",
  "lca.indicatorsEmpty": "No indicator declared yet",
  "lca.indicatorsEmptyHint":
    "Add the three headline metrics for a quick declaration, or the full core set for an EPD-grade profile.",
  "lca.count": "{n} / {max} indicators",
  "lca.incomplete": "{n} row(s) have no value or unit — they will not be saved.",
  "lca.rowIncomplete": "A value and a unit are required — this row will not be saved.",
  "lca.duplicate": "This code and module pair is declared twice.",
  "lca.unitMismatch": "EN 15804 unit for this indicator: {unit}",
  "lca.alreadyAdded": "Already added",
  "lca.maxReached": "At most 40 indicators can be declared.",
  "lca.unsaved": "Unsaved changes",
  "lca.publishCount": "{n} indicator(s) will be published",
  "lca.deleteConfirm": "Remove the LCA result?",
  "lca.deleteConfirmDesc": "The declaration and every indicator are deleted from the product.",

  "lcaHead.carbon": "Carbon footprint",
  "lcaHead.energy": "Energy (non-renewable)",
  "lcaHead.water": "Water use",

  "lcaGroup.impact": "Environmental impact",
  "lcaGroup.resource": "Resource use",
  "lcaGroup.waste": "Waste & output flows",

  "lcaInd.GWP_total": "Global warming potential — total",
  "lcaInd.GWP_fossil": "Global warming — fossil",
  "lcaInd.GWP_biogenic": "Global warming — biogenic",
  "lcaInd.GWP_luluc": "Global warming — land use change",
  "lcaInd.ODP": "Ozone depletion",
  "lcaInd.AP": "Acidification",
  "lcaInd.EP_freshwater": "Eutrophication — freshwater",
  "lcaInd.EP_marine": "Eutrophication — marine",
  "lcaInd.EP_terrestrial": "Eutrophication — terrestrial",
  "lcaInd.POCP": "Photochemical ozone formation",
  "lcaInd.ADPE": "Resource use — minerals & metals",
  "lcaInd.ADPF": "Resource use — fossil fuels",
  "lcaInd.WDP": "Water use (deprivation-weighted)",
  "lcaInd.PERE": "Renewable primary energy as energy",
  "lcaInd.PERM": "Renewable primary energy as material",
  "lcaInd.PERT": "Renewable primary energy — total",
  "lcaInd.PENRE": "Non-renewable primary energy as energy",
  "lcaInd.PENRM": "Non-renewable primary energy as material",
  "lcaInd.PENRT": "Non-renewable primary energy — total",
  "lcaInd.SM": "Secondary material",
  "lcaInd.RSF": "Renewable secondary fuels",
  "lcaInd.NRSF": "Non-renewable secondary fuels",
  "lcaInd.FW": "Net use of fresh water",
  "lcaInd.HWD": "Hazardous waste disposed",
  "lcaInd.NHWD": "Non-hazardous waste disposed",
  "lcaInd.RWD": "Radioactive waste disposed",
  "lcaInd.CRU": "Components for re-use",
  "lcaInd.MFR": "Materials for recycling",
  "lcaInd.MER": "Materials for energy recovery",
  "lcaInd.EEE": "Exported electrical energy",
  "lcaInd.EET": "Exported thermal energy",

  "lcaModGroup.product": "Product stage (A1–A3)",
  "lcaModGroup.construction": "Construction (A4–A5)",
  "lcaModGroup.use": "Use stage (B1–B7)",
  "lcaModGroup.endOfLife": "End of life (C1–C4)",
  "lcaModGroup.beyond": "Beyond the boundary (D)",

  "lcaMod.A1": "Raw material supply",
  "lcaMod.A2": "Transport",
  "lcaMod.A3": "Manufacturing",
  "lcaMod.A1-A3": "Product stage",
  "lcaMod.A4": "Transport to site",
  "lcaMod.A5": "Installation",
  "lcaMod.B1": "Use",
  "lcaMod.B2": "Maintenance",
  "lcaMod.B3": "Repair",
  "lcaMod.B4": "Replacement",
  "lcaMod.B5": "Refurbishment",
  "lcaMod.B6": "Operational energy use",
  "lcaMod.B7": "Operational water use",
  "lcaMod.C1": "Deconstruction",
  "lcaMod.C2": "Transport to waste processing",
  "lcaMod.C3": "Waste processing",
  "lcaMod.C4": "Disposal",
  "lcaMod.C1-C4": "End of life",
  "lcaMod.D": "Benefits and loads beyond the boundary",

  "lcaBoundary.cradle_to_gate": "Cradle to gate (A1–A3)",
  "lcaBoundary.cradle_to_gate_with_options": "Cradle to gate with options (A1–A3 + C, D)",
  "lcaBoundary.cradle_to_grave": "Cradle to grave (A–C)",
  "lcaBoundary.cradle_to_cradle": "Cradle to cradle",
  "lcaBoundary.gate_to_gate": "Gate to gate",

  "lcaFu.piece": "1 piece",
  "lcaFu.kg": "1 kg of product",
  "lcaFu.m2": "1 m² of fabric",
  "lcaFu.m": "1 m",
  "lcaFu.l": "1 L",
  "lcaFu.pair": "1 pair",
  "lcaFu.k1000": "1000 pieces",

  "partners.title": "Circularity partners",
  "partners.new": "New partner",
  "partners.edit": "Edit partner",
  "partners.saved": "Partner saved",
  "partners.deleted": "Partner removed",
  "partners.empty": "No partners yet — add donation, repair, recycling and resale points.",
  "partners.category": "Category",
  "partnerCat.donation": "Donation",
  "partnerCat.repair": "Repair",
  "partnerCat.upcycle": "Upcycling",
  "partnerCat.recycle": "Recycling",
  "partnerCat.resale": "Resale",
  "partners.categoryHint": "Pick a category or type your own to create a company-specific one.",
  "partners.categorySearch": "Search or type a new category…",
  "partners.categoryCreate": "Create category \u201c{name}\u201d",
  "partners.categoryCustom": "Your categories",
  "chain.productPartners": "Circularity partners for this product",

  "reviews.title": "Customer reviews",
  "reviews.empty": "No reviews.",
  "reviews.product": "Product",
  "reviews.author": "Author",
  "reviews.anonymous": "Anonymous",
  "reviews.rating": "Rating",
  "reviews.body": "Comment",
  "reviews.approve": "Approve",
  "reviews.reject": "Reject",
  "reviews.approvedToast": "Review approved",
  "reviews.rejectedToast": "Review rejected",
  "reviews.statusPending": "Pending",
  "reviews.statusApproved": "Approved",
  "reviews.statusRejected": "Rejected",
  "reviews.statusAll": "All",
  "reviews.status_pending": "Pending",
  "reviews.status_approved": "Approved",
  "reviews.status_rejected": "Rejected",
  "reviews.subtitle":
    "Reviews submitted from the public passport page wait here — nothing shows publicly until you approve it.",
  "reviews.searchPlaceholder": "Search product, author, or text",
  "reviews.avgRating": "Average rating",
  "reviews.avgRatingNote": "Approved reviews only",
  "reviews.noBody": "No comment text.",
  "reviews.emptyPending": "No pending reviews — all caught up.",
  "reviews.emptyFiltered": "No reviews match your search.",
  "reviews.rejectConfirm": "Reject this review?",
  "reviews.retract": "Take down",
  "reviews.retractConfirm": "Remove this review from the public page?",

  "dashboard.scans": "Passport page views",
  "dashboard.scansHint": "Daily public passport page views over the last 30 days (no visitor data stored).",
  "theme.title": "Public page theme",
  "theme.hint":
    "White-label the public passport pages: accent color, hidden sections, and custom section names.",
  "theme.saved": "Theme saved",
  "theme.accent": "Accent color",
  "theme.sections": "Sections (toggle off to hide, type to rename)",
  "theme.renamePlaceholder": "Custom name",
  "theme.hidden": "Hidden",
  "theme.sectionsVisible": "sections visible",
  "theme.reset": "Reset",
  "themeSection.highlights": "At a glance",
  "themeSection.operators": "Economic operators",
  "themeSection.documents": "Documents & instructions",
  "themeSection.identity": "Product identity",
  "themeSection.materials": "Materials",
  "themeSection.circularity": "Durability & circularity",
  "themeSection.use": "Care & end-of-life",
  "themeSection.supply-chain": "Supply chain",
  "themeSection.components": "Components",
  "themeSection.lca": "LCA",
  "themeSection.circularity-partners": "Circularity partners",
  "themeSection.certifications": "Certifications",
  "themeSection.reviews": "Reviews",

  "tier.public": "Public",
  "tier.restricted": "Restricted",
  "tier.label": "Visibility",
  "stage.design": "Design",
  "stage.raw_material": "Raw material",
  "stage.spinning": "Spinning",
  "stage.weaving": "Weaving",
  "stage.dyeing": "Dyeing & finishing",
  "stage.sampling": "Sampling",
  "stage.trims": "Trims",
  "stage.manufacturing": "Manufacturing",
  "stage.delivery": "Delivery",

  "media.title": "Images",
  "media.upload": "Upload image",
  "media.uploaded": "Image uploaded",
  "media.deleted": "Image removed",
  "media.empty": "No images yet — upload product photos for the public passport gallery.",
  "media.tooLarge": "Image exceeds the 5 MB limit",
  "media.hint": "JPEG, PNG or WebP, up to 5 MB and 10 images per product.",

  "documents.title": "Documents",
  "documents.hint":
    "Instructions for use, safety information, the EU declaration of conformity and certificates travel WITH the passport (PDF, up to 20 MB, 20 per product). Publishing freezes the current set; instructions stay online for the product's life. Nothing is deleted — revise or withdraw.",
  "documents.kind": "Type",
  "documents.titleLabel": "Title",
  "documents.titleRequired": "Give the document a title first.",
  "documents.lang": "Language",
  "documents.tier": "Visibility",
  "documents.size": "Size",
  "documents.referenceOnly": "reference",
  "documents.upload": "Upload PDF",
  "documents.tooLarge": "Document exceeds the 20 MB limit",
  "documents.uploaded": "Document saved",
  "documents.withdrawn": "Document withdrawn",
  "documents.withdrawConfirm": "Withdraw this document?",
  "documents.withdrawDescription":
    "Already published passports keep serving it; the next publish will leave it out. This cannot be undone.",
  "documents.open": "Open",
  "documents.empty": "No documents yet — attach at least the instructions for use.",
  "documents.addReference": "Add technical file reference",
  "documents.referenceHint":
    "The technical file itself is confidential and never uploaded — record where it is held (restricted-tier).",
  "documents.referenceNote": "Where the file is held",
  "documents.referenceUrl": "Link (optional)",
  "documents.status.current": "current",
  "documents.status.superseded": "superseded",
  "documents.status.withdrawn": "withdrawn",

  "documentKind.instructions": "Instructions for use",
  "documentKind.safety_information": "Safety information",
  "documentKind.declaration_of_conformity": "EU declaration of conformity",
  "documentKind.test_report": "Test report",
  "documentKind.certificate": "Certificate",
  "documentKind.authorised_rep_mandate": "Authorised representative mandate",
  "documentKind.technical_file_reference": "Technical file reference",
  "documentKind.other": "Other",

  "readiness.title": "Readiness checks",
  "readiness.block": "blocks publishing",
  "readiness.warn": "warning",
  "product.readOnly": "You do not have permission to edit products — the form is read-only.",
  "product.identifiers": "Identifiers",
  "product.assignIdentifier": "Assign identifier",
  "product.identifierAssigned": "Identifier assigned",
  "product.scheme": "Scheme",
  "product.granularity": "Granularity",
  "product.serial": "Serial",
  "product.serialHelp": "Required for item-granularity GS1 identifiers.",
  "product.identifierValue": "Value",
  "product.noIdentifiers": "No identifiers assigned",
  "product.lot": "Lot / batch",
  "product.lotHelp": "Required for batch-granularity GS1 identifiers (AI 10); optional on item level.",
  "product.identifierStatus": "Status",
  "product.withdrawIdentifier": "Withdraw",
  "product.withdrawConfirm": "Withdraw this identifier?",
  "product.withdrawDescription":
    "The QR code keeps resolving, but the passport page will flag the identifier as withdrawn and no new passport can use it. This cannot be undone.",
  "product.identifierWithdrawn": "Identifier withdrawn",

  "identifierStatus.active": "Active",
  "identifierStatus.withdrawn": "Withdrawn",

  "idScheme.gs1": "GS1 Digital Link",
  "idScheme.internal": "Internal",
  "idGranularity.model": "model",
  "idGranularity.batch": "batch",
  "idGranularity.item": "item",

  "import.title": "CSV import",
  "import.hint":
    "Expected columns: sku, name, gtin (optional), gtip (optional 12-digit customs code; commodityCode is accepted too), schemaId — plus any attribute columns addressed by field id (e.g. identity.brand). Rows are upserted by SKU.",
  "import.pasteLabel": "Paste CSV",
  "import.fileLabel": "…or upload a .csv file or an IDPP Excel data template (.xlsx)",
  "import.xlsxLoaded": "{n} product sheet(s) read from the Excel file — review and import",
  "import.xlsxEmpty": "No filled product sheet found in the Excel file",
  "import.sheet": "Sheet",
  "import.run": "Import",
  "import.created": "Created",
  "import.updated": "Updated",
  "import.errorCount": "Errors",
  "import.errorsTitle": "Row errors",
  "import.row": "Row",
  "import.message": "Message",
  "import.done": "Import finished",
  "products.excelImport": "Import from Excel",
  "excelImport.title": "Import from Excel",
  "excelImport.steps":
    "1. Pick your passport type and download its Excel template. 2. Fill one sheet per product (copy the 'Ürün 1' sheet for more products). 3. Upload the file, review and complete the data, then save.",
  "excelImport.template": "Passport template",
  "excelImport.download": "Download Excel template",
  "excelImport.upload": "Upload the filled Excel file (.xlsx)",
  "excelImport.toFix": "{n} need attention",
  "excelImport.ready": "{n} ready to save",
  "excelImport.fixHint": "Complete the highlighted fields; only complete products are saved.",
  "excelImport.allReady": "No missing data — every product is ready to save.",
  "excelImport.updateNote": "Products whose SKU already exists are updated.",
  "excelImport.status.ready": "Ready",
  "excelImport.status.issues": "{n} field(s) missing or invalid",
  "excelImport.status.fault": "Rejected by the server",
  "excelImport.required": "Required",
  "excelImport.invalid.number": "Enter a number",
  "excelImport.invalid.percentage": "Enter a number between 0 and 100",
  "excelImport.invalid.enum": "Pick a value from the list",
  "excelImport.invalid.date": "Enter a valid date",
  "excelImport.invalid.url": "Enter a link starting with http:// or https://",
  "excelImport.invalid.boolean": "Pick Yes or No",
  "excelImport.invalid.text": "Invalid value",
  "excelImport.sku": "SKU",
  "excelImport.name": "Product name",
  "excelImport.skuInvalid": "Max 64 characters, and unique within the file",
  "excelImport.nameInvalid": "Max 200 characters",
  "excelImport.unknownSchema": "Unknown passport template: {id}",
  "excelImport.remove": "Remove from list",
  "excelImport.back": "Upload another file",
  "excelImport.save": "Save all ({n})",
  "excelImport.saved": "{n} product(s) saved",
  "excelImport.rejected": "{n} product(s) were rejected by the server — fix them and save again",
  "excelImport.orphans":
    "{n} list row(s) match no product sheet by SKU and will not be imported: {rows}. Check the SKU column on the list sheets.",

  "suppliers.title": "Suppliers",
  "suppliers.invite": "Invite supplier",
  "suppliers.selectAll": "Select all",
  "suppliers.clear": "Clear",
  "suppliers.inviteTitle": "New supplier invite",
  "suppliers.name": "Supplier name",
  "suppliers.email": "Supplier email",
  "suppliers.emailHelp": "Optional — no email is sent yet; you copy the link and share it yourself.",
  "suppliers.fields": "Requested fields",
  "suppliers.fieldsHelp": "Select the passport fields the supplier should fill in.",
  "suppliers.expiresInDays": "Valid for (days)",
  "suppliers.expiresHelp": "1–90 days. The link stops working after this.",
  "suppliers.note": "Internal note",
  "suppliers.created": "Invite created",
  "suppliers.linkTitle": "Share link",
  "suppliers.linkOnce": "This link is shown only once — copy it now and send it to the supplier.",
  "suppliers.copyLink": "Copy link",
  "suppliers.linkCopied": "Link copied",
  "suppliers.linkUnavailable": "Links are shown only once, when the invite is created.",
  "suppliers.empty": "No supplier invites yet",
  "suppliers.supplier": "Supplier",
  "suppliers.expiry": "Expires",
  "suppliers.fieldCount": "Fields",
  "suppliers.review": "Review",
  "suppliers.revoke": "Revoke",
  "suppliers.revokeConfirm": "Revoke this invite?",
  "suppliers.revokeDescription": "The supplier's link stops working immediately.",
  "suppliers.revoked": "Invite revoked",
  "suppliers.reviewTitle": "Supplier submission",
  "suppliers.submittedBy": "Submitted by",
  "suppliers.submittedAt": "Submitted",
  "suppliers.appliedAt": "Applied",
  "suppliers.field": "Field",
  "suppliers.submittedValue": "Submitted value",
  "suppliers.currentValue": "Current value",
  "suppliers.changed": "changed",
  "suppliers.same": "same",
  "suppliers.apply": "Apply selected",
  "suppliers.applied": "Submission applied to the product",
  "suppliers.selectAtLeastOne": "Select at least one field to apply",
  "suppliers.selectFieldRequired": "Select at least one field to request",

  "supplierStatus.pending": "pending",
  "supplierStatus.submitted": "submitted",
  "supplierStatus.applied": "applied",
  "supplierStatus.revoked": "revoked",
  "supplierStatus.expired": "expired",

  "access.title": "Restricted access",
  "access.grant": "Grant access",
  "access.grantTitle": "New access grant",
  "access.intro":
    "Grants a repairer, recycler, customs authority or partner a link that also shows this passport's restricted fields.",
  "access.role": "Role",
  "access.holder": "Issued to",
  "access.holderHelp": "The organization or person this access is issued to (internal bookkeeping).",
  "access.holderRequired": "Enter who this access is issued to",
  "access.expiresInDays": "Valid for (days)",
  "access.expiresHelp": "1–365 days. The link stops working after this.",
  "access.created": "Access grant created",
  "access.linkTitle": "Access link",
  "access.linkOnce": "This link is shown only once — copy it now and send it to the holder.",
  "access.copyLink": "Copy link",
  "access.linkCopied": "Link copied",
  "access.empty": "No access grants yet",
  "access.expiry": "Expires",
  "access.lastAccessed": "Last accessed",
  "access.revoke": "Revoke",
  "access.revokeConfirm": "Revoke this access grant?",
  "access.revokeDescription": "The holder's link stops working immediately.",
  "access.revoked": "Access grant revoked",

  "accessRole.repairer": "Repairer",
  "accessRole.recycler": "Recycler",
  "accessRole.customs": "Customs",
  "accessRole.partner": "Partner",
  "accessRole.authority": "Competent authority",
  "accessRole.refurbisher": "Refurbisher / remanufacturer",
  "accessRole.distributor": "Distributor",

  "operators.title": "Economic operators",
  "operators.hint":
    "The parties your passports name — manufacturer, importer, authorised representative, responsible person, DPP service provider. One default per role; products can override. Manufacturer and importer contact details appear on the public passport page.",
  "operators.new": "New operator",
  "operators.edit": "Edit operator",
  "operators.saved": "Operator saved",
  "operators.deleted": "Operator removed",
  "operators.deleteConfirm":
    "Remove this operator? Published passports keep the copy they were published with.",
  "operators.empty": "No operators yet — add at least the manufacturer (your own company).",
  "operators.role": "Role",
  "operators.default": "default",
  "operators.self": "this organization",
  "operators.setDefault": "Org default for this role",
  "operators.legalName": "Registered legal name",
  "operators.tradeName": "Trade name",
  "operators.streetAddress": "Street address",
  "operators.postalCode": "Postal code",
  "operators.region": "Region",
  "operators.email": "E-mail",
  "operators.phone": "Phone",
  "operators.website": "Website",
  "operators.identifier": "Identifier",
  "operators.identifierScheme": "Identifier scheme",
  "operators.identifierValue": "Identifier",
  "operators.identifierHelp": "GLN 13 digits, MERSİS 16, VKN 10, LEI 20, EORI/VAT country code + digits.",
  "operators.noIdentifier": "None",
  "operators.eori": "EORI",
  "operators.eoriHelp": "The importer's EU customs number (country code + up to 15 characters).",
  "operators.contactTier": "Contact visibility",
  "operators.contactTierHelp": "Public: shown on the passport page. Restricted: only behind an access grant.",
  "operators.tier.public": "public",
  "operators.tier.restricted": "restricted",
  "operators.mandateFrom": "Mandate valid from",
  "operators.mandateUntil": "Mandate valid until",
  "operators.productTitle": "Economic operators of this product",
  "operators.productHint":
    "Override the organization defaults for this product. Publishing freezes the effective party per role into the passport.",
  "operators.useDefault": "Org default",
  "operators.noneNamed": "Nobody named",
  "operators.assigned": "Operators assigned",
  "operators.source.product": "product override",
  "operators.source.default": "org default",
  "operators.source.none": "none",

  "operatorRole.manufacturer": "Manufacturer",
  "operatorRole.importer": "Importer",
  "operatorRole.authorised_representative": "Authorised representative",
  "operatorRole.responsible_person": "Responsible person",
  "operatorRole.dpp_service_provider": "DPP service provider",
  "operatorRole.distributor": "Distributor",

  "operatorScheme.GLN": "GS1 GLN",
  "operatorScheme.EORI": "EORI",
  "operatorScheme.VAT": "VAT number",
  "operatorScheme.LEI": "LEI",
  "operatorScheme.NTR": "Trade register no",
  "operatorScheme.MERSIS": "MERSİS",
  "operatorScheme.VKN": "Tax ID (VKN)",
  "operatorScheme.DUNS": "D-U-N-S",
  "operatorScheme.LOCAL": "Other / local",

  "accessStatus.active": "active",
  "accessStatus.revoked": "revoked",
  "accessStatus.expired": "expired",

  "passports.title": "Passports",
  "passports.new": "Create passport",
  "passports.exportCustomsCsv": "Customs list (CSV)",
  "passports.createTitle": "New passport",
  "passports.created": "Passport created",
  "passports.product": "Product",
  "passports.version": "Version",
  "passports.filterAll": "All products",
  "passports.empty": "No passports yet",
  "passports.selectProduct": "Select a product…",
  "passports.qr": "QR",
  "passports.qrShow": "Show QR",
  "passports.qrHint": "Scan to open the public passport page",

  "passportStatus.draft": "draft",
  "passportStatus.published": "published",
  "passportStatus.archived": "archived",

  "passport.notFound": "Passport not found",
  "passport.backToList": "All passports",
  "passport.publish": "Publish",
  "passport.republish": "Republish",
  "passport.published": "Passport published",
  "passport.publishBlocked": "Publishing is blocked — missing or invalid fields:",
  "passport.archiveConfirm": "Archive this passport?",
  "passport.archiveDescription": "An archived passport can no longer be published.",
  "passport.archived": "Passport archived",
  "passport.versions": "Versions",
  "passport.noVersions": "Not published yet",
  "passport.verify": "Verify chain",
  "passport.chainValid": "Version chain intact",
  "passport.chainBroken": "Chain broken at version",
  "passport.publicPage": "Public page",
  "passport.qr": "Download QR",
  "passport.currentVersion": "Current version",
  "passport.productCompliance": "Current product compliance",
  "passport.publishedCompliance": "Published compliance",
  "passport.drift":
    "The product data changed since the last publish — republish to update the public passport.",
  "passport.hash": "Hash",
  "passport.registry": "EU DPP Registry",
  "passport.register": "Register",
  "passport.registered": "Passport registered in the EU DPP Registry",
  "passport.registerFailed": "Registration failed",
  "passport.notRegistered": "Not registered",
  "passport.registeredAt": "Registered",
  "passport.registryUid": "Registered UID",
  "passport.registryAttempts": "attempts",
  "passport.registerHint": "Only a published passport can be registered.",
  "passport.euMissing":
    "The EU DPP Registry will reject this passport: fill these fields and publish a new version.",
  "passport.euMissingRegistrant": "Registrant identity (complete the EU registry enrolment)",
  "passport.euMissingManufacturer": "Manufacturer postal address (add a public manufacturer operator)",
  "passport.customs": "Customs & registration",
  "passport.customsHint":
    "Put the registration URI and the commodity codes on the export declaration — customs matches them against the registry.",
  "passport.customsUpi": "UPI (unique product identifier)",
  "passport.customsUri": "Registration URI",
  "passport.customsUriPending": "Assigned by the registry after validation",
  "passport.customsCn": "CN commodity code",
  "passport.customsGtip": "GTİP",
  "passport.customsNone": "Not registered yet — the UPI and URI appear here once the passport is registered.",
  "passport.retention": "Retention & backup",
  "passport.retentionHint":
    "The passport, its documents and instructions must stay online for {years} years after the LAST unit was placed on the market. The first publish sets the placing date; correct it or add the last-unit date here.",
  "passport.placedOnMarket": "Placed on the market",
  "passport.lastUnitPlacedOnMarket": "Last unit placed on the market",
  "passport.retainUntil": "Keep online until",
  "passport.datesSaved": "Market dates saved",
  "passport.backup": "Backup copy",
  "passport.backupNotConfigured": "backup host not configured",
  "passport.backupNone": "no copy yet",
  "passport.backupStoredAt": "Copy stored",
  "backupStatus.pending": "pending",
  "backupStatus.stored": "stored at backup host",
  "backupStatus.failed": "failed — will retry",
  "org.status": "Status",
  "orgStatus.active": "active",
  "orgStatus.suspended": "suspended",
  "orgStatus.wound_down": "wound down — read-only, passports stay online",
  "audience.label": "Who can see this",
  "audience.help":
    "Restricted items only. Nothing checked = every access role; the competent authority always sees everything.",
  "audience.everyone": "every restricted role",
  "passport.lineage": "Lineage",
  "passport.lineageHint":
    "A passport for a refurbished, remanufactured or repurposed unit links to the original passport (same organization by id, another operator by URL). Published on the next publish.",
  "passport.originalPassport": "Original passport",
  "passport.lineageReason": "Reason",
  "passport.lineageSaved": "Lineage saved",
  "lineageReason.refurbished": "refurbished",
  "lineageReason.remanufactured": "remanufactured",
  "lineageReason.repurposed": "repurposed",
  "lineageReason.reissued": "reissued",
  "passport.exportOfficial": "Official dump",
  "passport.exportHint": "Full-tier copy for the competent authority (Art. 27(15)) — audited.",
  "passport.viewJson": "View JSON",
  "passport.downloadJsonLd": "Download JSON-LD",
  "jsonViewer.search": "Search in JSON",
  "jsonViewer.mode": "View mode",
  "jsonViewer.tree": "Tree",
  "jsonViewer.raw": "Raw",
  "jsonViewer.expandAll": "Expand all",
  "jsonViewer.collapseAll": "Collapse all",
  "jsonViewer.expand": "Expand",
  "jsonViewer.collapse": "Collapse",
  "jsonViewer.copy": "Copy",
  "jsonViewer.copied": "Copied",
  "jsonViewer.matches": "Matches: {count}",
  "jsonViewer.noMatches": "No matches",
  "org.enterpriseSize": "Enterprise size",
  "org.enterpriseSizeSaved": "Enterprise size saved",
  "enterpriseSize.micro": "micro (< 10 staff)",
  "enterpriseSize.small": "small (< 50 staff)",
  "enterpriseSize.medium": "medium (< 250 staff)",
  "enterpriseSize.large": "large (250+ staff)",
  "disclosure.title": "Unsold consumer goods — destruction disclosure",
  "disclosure.hint":
    "ESPR / draft Turkish regulation Art. 24: each year, publish the number and weight of unsold consumer products discarded, the reasons, the exemption relied on, the share sent to each treatment and the measures taken. Publishing freezes the year and lists it on the public page below.",
  "disclosure.sizeUnknown":
    "Declare your enterprise size in Organization settings — it decides whether the destruction ban applies to you.",
  "disclosure.banApplies":
    "The destruction ban for apparel, accessories and footwear applies to your organization since {date}.",
  "disclosure.banFuture":
    "The destruction ban will apply to your organization from {date}. Disclosure is voluntary until then.",
  "disclosure.exempt":
    "Micro and small enterprises are exempt from the destruction ban — disclosure is voluntary.",
  "disclosure.publicPage": "Public page",
  "disclosure.noReports": "No disclosure yet — pick a fiscal year below and add lines.",
  "disclosure.year": "Fiscal year",
  "disclosure.lines": "Lines",
  "disclosure.publishedAt": "Published",
  "disclosure.status.draft": "draft",
  "disclosure.status.published": "published",
  "disclosure.saveDraft": "Save draft",
  "disclosure.saved": "Draft saved",
  "disclosure.publish": "Publish",
  "disclosure.published": "Disclosure published",
  "disclosure.publishConfirm": "Publish this year's disclosure?",
  "disclosure.publishDescription":
    "A published year is frozen and appears on the public page. This cannot be undone.",
  "disclosure.locked": "This year is published and frozen.",
  "disclosure.note": "Note (optional)",
  "disclosure.category": "Product type",
  "disclosure.categoryNote": "Type detail",
  "disclosure.units": "Units discarded",
  "disclosure.weightKg": "Weight (kg)",
  "disclosure.reason": "Reason",
  "disclosure.reasonNote": "Reason detail",
  "disclosure.exemptionRef": "Exemption relied on",
  "disclosure.reusePct": "Prepared for reuse",
  "disclosure.recycledPct": "Recycled",
  "disclosure.otherRecoveryPct": "Other recovery",
  "disclosure.disposedPct": "Disposal",
  "disclosure.sharesInvalid": "shares must total 100 %",
  "disclosure.measures": "Measures taken or planned to avoid destruction",
  "disclosure.addLine": "Add line",
  "disposalCategory.apparel": "Apparel",
  "disposalCategory.clothing_accessories": "Clothing accessories",
  "disposalCategory.footwear": "Footwear",
  "disposalCategory.other": "Other",
  "disposalReason.health_safety": "Health, hygiene or safety",
  "disposalReason.damaged_unrepairable": "Damage not repairable at reasonable cost",
  "disposalReason.unfit_for_purpose": "Unfit for its intended purpose",
  "disposalReason.donation_refused": "Donation refused",
  "disposalReason.not_reusable": "Not suitable for reuse or remanufacturing",
  "disposalReason.ip_infringement": "IP infringement (counterfeit)",
  "disposalReason.least_impact_option": "Least environmental impact option",
  "disposalReason.other": "Other",
  "passport.readiness": "Publication status",
  "passport.checkComplete": "Every required field is filled",
  "passport.checkMissing": "{count} required field(s) still missing",
  "passport.checkPublished": "Published as v{version}",
  "passport.checkUnpublished": "Not published — the public page is not live yet",
  "passport.checkFresh": "The public page matches the current product data",
  "passport.checkArchived": "Archived — this passport can no longer be published",
  "passport.missingFields": "Missing required fields",
  "passport.openProduct": "Open product",
  "passport.publicAccess": "Public page & QR",
  "passport.publicUrl": "Public address",
  "passport.copyLink": "Copy link",
  "passport.linkCopied": "Link copied",
  "passport.notPublicYet": "The public page and its QR code go live once you publish this passport.",
  "passport.identity": "Identity",
  "passport.identifier": "Identifier",
  "passport.versionsHint": "Every publish appends a hash-chained, tamper-evident version.",
  "passport.latest": "current",
  "passport.moreFields": "+{count} more",

  "registryStatus.pending": "Pending",
  "registryStatus.processing": "Awaiting registry validation",
  "registryStatus.registered": "Registered",
  "registryStatus.failed": "Failed",

  "orgSettings.title": "Organization settings",
  "orgSettings.members": "Members",
  "orgSettings.addMember": "Add member",
  "orgSettings.memberAdded": "Member added",
  "orgSettings.memberRemoved": "Member removed",
  "orgSettings.roleChanged": "Role updated",
  "orgSettings.removeConfirm": "Remove this member?",
  "orgSettings.removeDescription": "The user loses access to this organization.",
  "orgSettings.user": "User",
  "orgSettings.email": "Email",
  "orgSettings.role": "Role",
  "orgSettings.noMembers": "No members",
  "orgSettings.yourRole": "Your role",
  "orgSettings.usage": "Passports published this year",

  "apiKeys.title": "API keys",
  "apiKeys.create": "Create key",
  "apiKeys.createTitle": "New API key",
  "apiKeys.intro":
    "Gives an ERP connector (Logo, Nebim, SAP…) machine access to this organization's data. The key's scopes are its only permissions.",
  "apiKeys.name": "Name",
  "apiKeys.nameHelp": 'A label for the integration, e.g. "Logo connector".',
  "apiKeys.nameRequired": "Enter a name for the key",
  "apiKeys.scopes": "Scopes",
  "apiKeys.scopeRequired": "Select at least one scope",
  "apiKeys.expiresInDays": "Valid for (days)",
  "apiKeys.expiresHelp": "Optional — leave empty for a key that does not expire.",
  "apiKeys.created": "API key created",
  "apiKeys.tokenTitle": "API key",
  "apiKeys.tokenOnce": "This key is shown only once — copy it now and store it in your connector.",
  "apiKeys.copy": "Copy key",
  "apiKeys.copied": "Copied",
  "apiKeys.empty": "No API keys yet",
  "apiKeys.keyPrefix": "Key",
  "apiKeys.lastUsed": "Last used",
  "apiKeys.expiry": "Expires",
  "apiKeys.noExpiry": "never",
  "apiKeys.revoke": "Revoke",
  "apiKeys.revokeConfirm": "Revoke this API key?",
  "apiKeys.revokeDescription": "The connector using this key stops working immediately.",
  "apiKeys.revoked": "API key revoked",

  "apiKeyStatus.active": "active",
  "apiKeyStatus.revoked": "revoked",
  "apiKeyStatus.expired": "expired",

  "webhooks.title": "Webhooks",
  "webhooks.create": "Add webhook",
  "webhooks.createTitle": "New webhook",
  "webhooks.intro":
    "Sends signed event notifications (HMAC-SHA256 over the request body) to your endpoint when passports or products change.",
  "webhooks.url": "Endpoint URL",
  "webhooks.urlHelp": "HTTPS endpoint that receives the signed POST requests.",
  "webhooks.urlRequired": "Enter the endpoint URL",
  "webhooks.events": "Events",
  "webhooks.eventRequired": "Select at least one event",
  "webhooks.created": "Webhook created",
  "webhooks.secretTitle": "Signing secret",
  "webhooks.secretOnce":
    "This signing secret is shown only once — copy it now and use it to verify the X-DPP-Signature header.",
  "webhooks.copy": "Copy secret",
  "webhooks.copied": "Copied",
  "webhooks.empty": "No webhooks yet",
  "webhooks.lastDelivery": "Last delivery",
  "webhooks.test": "Send test",
  "webhooks.testOk": "Test ping delivered",
  "webhooks.testFailed": "Test ping failed",
  "webhooks.enable": "Enable",
  "webhooks.enabled": "Webhook enabled",
  "webhooks.disable": "Disable",
  "webhooks.disabled": "Webhook disabled",
  "webhooks.disableConfirm": "Disable this webhook?",
  "webhooks.disableDescription": "No further events are delivered until it is re-enabled.",
  "webhooks.deliveries": "Deliveries",
  "webhooks.deliveriesTitle": "Webhook deliveries",
  "webhooks.noDeliveries": "No deliveries yet",
  "webhooks.event": "Event",
  "webhooks.attempts": "Attempts",
  "webhooks.lastError": "Last error",
  "webhooks.retry": "Retry",
  "webhooks.retryOk": "Delivery succeeded",
  "webhooks.retryFailed": "Delivery failed again",

  "webhookStatus.active": "active",
  "webhookStatus.disabled": "disabled",

  "deliveryStatus.pending": "pending",
  "deliveryStatus.delivered": "delivered",
  "deliveryStatus.failed": "failed",

  "role.owner": "owner",
  "role.member": "member",
  "role.admin": "admin",
  "role.editor": "editor",
  "role.viewer": "viewer",

  "login.title": "Sign in",
  "login.continue": "Continue",
  "login.password": "Password",
  "login.newPassword": "New password",
  "login.updatePassword": "Update password",
  "login.passwordExpired": "Your password has expired. Set a new one to continue.",
  "login.passwordUpdated": "Password updated. Please sign in with your new password.",
  // Shown after every sign-up. Worded to fit BOTH outcomes — new account and
  // already-registered address — because the API deliberately does not tell the
  // client which one happened (anti user-enumeration).
  "login.registerPending":
    "Check your email to continue, then sign in below. If this address already has an account, we've emailed you about it instead.",
  "login.mfaPrompt": "Enter the 6-digit code from your authenticator app.",
  "login.mfaCode": "Authentication code",
  "login.verify": "Verify",
  "login.requestFailed": "Request failed",
  "login.subtitle": "Sign in to your organization's panel.",
  "login.explore": "New here? Discover the product →",
  "login.register": "Create account",
  "login.noAccount": "No account? Sign up →",
  "login.haveAccount": "Already have an account? Sign in →",
  "login.registerTitle": "Create your account",
  "login.registerSubtitle": "Start issuing digital product passports in minutes.",
  "login.mfaTitle": "Two-step verification",
  "login.expiredTitle": "Set a new password",
  "login.back": "← Back to sign in",
  "login.forgotPassword": "Forgot password?",
  "login.name": "Name",
  "login.nameHelp": "Optional — shown to your teammates.",
  "login.emailPlaceholder": "you@company.com",
  "login.pwdWeak": "The password doesn't meet the requirements below yet.",
  "login.pwdRule.length": "At least 12 characters",
  "login.pwdRule.lower": "A lowercase letter",
  "login.pwdRule.upper": "An uppercase letter",
  "login.pwdRule.digit": "A digit",
  "login.pwdRule.symbol": "A symbol",
  "login.inviteNotice": "Sign in or create an account to continue to your invitation.",
  "invites.title": "Invitations",
  "invites.create": "Invite",
  "invites.createTitle": "Invite a teammate",
  "invites.intro": "Send an email invitation to join this organization at a chosen role.",
  "invites.email": "Email",
  "invites.emailRequired": "An email address is required",
  "invites.send": "Send invite",
  "invites.created": "Invitation created",
  "invites.linkTitle": "Invitation link",
  "invites.linkReady": "Invitation sent. You can also share this one-time link:",
  "invites.copy": "Copy link",
  "invites.copied": "Link copied",
  "invites.expiry": "Expires",
  "invites.empty": "No invitations yet",
  "invites.revoke": "Revoke",
  "invites.revokeConfirm": "Revoke this invitation?",
  "invites.revoked": "Invitation revoked",
  "invites.acceptTitle": "You're invited",
  "invites.acceptBody": "You've been invited to join",
  "invites.acceptButton": "Accept invitation",
  "invites.acceptSuccess": "You've joined the organization",
  "invites.acceptSignIn": "Sign in or create an account",
  "invites.acceptSignInPrompt": "Sign in or register to accept — use this email:",
  "invites.acceptWrongAccount": "You're signed in with a different account. Sign in with:",
  "invites.acceptMissingToken": "This invitation link is missing its token.",
  "invites.acceptErrorTitle": "Invitation unavailable",
  "invites.acceptSwitch": "Sign out and switch account",
  "invites.loading": "Loading your invitation…",
  "inviteStatus.pending": "Pending",
  "inviteStatus.accepted": "Accepted",
  "inviteStatus.revoked": "Revoked",
  "inviteStatus.expired": "Expired",
  "verify.missingToken": "This verification link is missing its token.",
  "verify.successTitle": "Email verified",
  "verify.successBody": "Thanks — your email address is confirmed.",
  "verify.errorTitle": "Verification failed",
  "verify.goDashboard": "Go to dashboard",
  "verify.goLogin": "Go to sign in",
  "verify.resend": "Resend",
  "verify.resent": "Verification email sent",
  "verify.bannerText": "Please verify your email address.",
  "verify.verifying": "Verifying your email address…",
  "verify.enterCode": "Enter code",
  "verify.modalTitle": "Verify your email",
  "verify.modalBody": "We emailed a 6-digit verification code to:",
  "verify.codeLabel": "Verification code",
  "verify.codeInvalid": "Enter the 6-digit code from the email.",
  "verify.submit": "Verify",
  "verify.verified": "Your email address is verified.",
  "forgot.title": "Reset your password",
  "forgot.subtitle": "Enter your account email and we'll send you a link to set a new password.",
  "forgot.submit": "Send reset link",
  "forgot.sentTitle": "Check your email",
  "forgot.sentBody":
    "If an account exists for that email, a password-reset link is on its way. The link expires shortly, so use it soon.",
  "reset.title": "Set a new password",
  "reset.subtitle": "Choose a strong new password for your account.",
  "reset.submit": "Update password",
  "reset.successTitle": "Password updated",
  "reset.successBody": "Your password has been reset. Sign in with your new password.",
  "reset.errorTitle": "Reset link problem",
  "reset.missingToken": "This reset link is missing its token. Request a new one to continue.",
  "reset.requestNew": "Request a new link",
  "nav.billing": "Billing",
  "billing.usageTitle": "Plan & usage",
  "billing.plan": "Plan",
  "billing.period": "Period",
  "billing.kind": "Type",
  "billing.annualFee": "Annual fee",
  "billing.viewsThisYear": "Views this year",
  "billing.viewsThisMonth": "Views this month",
  "billing.products": "Products",
  "billing.unlimited": "Unlimited",
  "billing.adminTitle": "Operator actions",
  "billing.adminHint": "Close the previous month to generate draft invoices, then issue and collect them.",
  "billing.closeMonth": "Close previous month",
  "billing.monthClosed": "Month closed — draft invoices generated",
  "billing.invoicesTitle": "Invoices",
  "billing.noInvoices": "No invoices yet",
  "billing.views": "Views",
  "billing.amount": "Amount",
  "billing.due": "Due",
  "billing.issue": "Issue",
  "billing.issued": "Invoice issued",
  "billing.markPaid": "Mark paid",
  "billing.markedPaid": "Invoice marked paid",
  "billing.void": "Void",
  "billing.voided": "Invoice voided",
  "billing.voidConfirm": "Void this invoice?",
  "billing.delinquentNotice":
    "This organization has an overdue invoice. Editing is locked until it is paid; public passport pages stay online.",
  "billing.delinquentBanner": "An invoice is overdue — editing is locked until it is paid.",
  "billing.viewLimitNotice":
    "The free plan's yearly view allowance is used up. Editing is locked until you upgrade; public passport pages stay online.",
  "billing.viewLimitBanner": "Free plan view allowance used up — upgrade to keep editing.",
  "billing.viewInvoices": "View invoices →",
  "billing.disabled": "Billing is not available in this deployment.",
  "invoiceKind.subscription": "Subscription",
  "invoiceKind.usage": "Usage",
  "invoiceStatus.draft": "Draft",
  "invoiceStatus.issued": "Issued",
  "invoiceStatus.paid": "Paid",
  "invoiceStatus.overdue": "Overdue",
  "invoiceStatus.void": "Void",
  "login.pitch1": "Standards-native passports: GS1 Digital Link, JSON-LD, EN 1821x",
  "login.pitch2": "Signed, versioned, immutable publications with a Verified badge",
  "login.pitch3": "Supply chain, components, LCA and circularity on one public page",
  "login.pitch4": "Layered access for consumers, repairers, recyclers and customs",

  "dashboard.account": "Account",
  "dashboard.name": "Name",
  "dashboard.mfaEnabled": "Enabled ✅",
  "dashboard.mfaDisabled": "Disabled ⚠️",
  "dashboard.downloadData": "Download my data (GDPR)",
  "dashboard.sessions": "Active sessions",
  "dashboard.sessionsEmpty": "No active sessions",
  "dashboard.sessionStarted": "Started",
  "dashboard.thisDevice": "this device",
  "dashboard.revoke": "Revoke",
  "dashboard.permissions": "Permissions",
  "dashboard.noPermissions": "No collection-level permissions.",
  "dashboard.mfaTitle": "Enable two-factor authentication",
  "dashboard.mfaStepStart": "Start",
  "dashboard.mfaStepVerify": "Scan & verify",
  "dashboard.mfaStepEnabled": "Enabled",
  "dashboard.mfaStart": "Start MFA setup",
  "dashboard.mfaSecretHint": "Add this secret to your authenticator app, then enter the current code.",
  "dashboard.mfaConfirm": "Confirm",
  "dashboard.mfaDone": "MFA enabled. You'll be asked for a code next sign-in.",
  "dashboard.mfaEnrollFailed": "Failed to start enrollment",
  "dashboard.mfaInvalidCode": "Invalid code",

  "profile.tabProfile": "Account",
  "profile.tabSecurity": "Security",
  "profile.tab2fa": "Two-factor",
  "profile.tabDevices": "Devices",
  "profile.tabOrgs": "Organizations",
  "profile.tabActivity": "Activity",
  "profile.accountInfo": "Account details",
  "profile.identity": "Identity",
  "profile.displayName": "Display name",
  "profile.displayNameHint": "The name shown across the app.",
  "profile.saved": "Profile updated.",
  "profile.security": "Password",
  "profile.password": "Password",
  "profile.currentPassword": "Current password",
  "profile.newPassword": "New password",
  "profile.confirmNewPassword": "Confirm new password",
  "profile.changePassword": "Change password",
  "profile.passwordMismatch": "New passwords do not match.",
  "profile.passwordChanged": "Password changed — please sign in again.",
  "profile.passwordChangeWarning": "Changing your password signs you out of every device.",
  "profile.mfaActive": "Two-factor is on",
  "profile.mfaActiveHint": "Your account is protected by an authenticator app.",
  "profile.mfaOffHint": "Add a second factor to better protect your account.",
  "profile.mfaDisable": "Disable two-factor",
  "profile.mfaDisableHint": "Enter your password and a current code to turn it off.",

  "profile.memberSince": "Member since {date}",
  "profile.emailVerified": "Verified",
  "profile.emailUnverified": "Unverified",
  "profile.verifyNow": "Verify",
  "profile.demoAccount": "Demo account",
  "profile.securityScore": "Account security",
  "profile.scoreStrong": "Strong",
  "profile.scoreFair": "Fair",
  "profile.scoreWeak": "Needs attention",
  "profile.userId": "User ID",
  "profile.permissions": "Permissions",
  "profile.permissionsCount": "{count} granted",
  "profile.preferences": "Preferences",
  "profile.preferencesHint": "Language and theme are remembered on this device only.",
  "profile.theme": "Theme",
  "profile.themeLight": "Light",
  "profile.themeDark": "Dark",
  "profile.privacy": "Data & privacy",
  "profile.privacyHint":
    "Download everything stored about your account — profile, sessions and account events — as JSON.",

  "profile.checklist": "Security checklist",
  "profile.checklistDone": "{done} of {total} done",
  "profile.checkEmail": "Verify your email address",
  "profile.checkEmailDone": "Your address is confirmed.",
  "profile.checkEmailTodo": "Confirm it so account recovery can reach you.",
  "profile.checkMfa": "Turn on two-factor authentication",
  "profile.checkMfaDone": "An authenticator app guards your sign-in.",
  "profile.checkMfaTodo": "A password alone is a single point of failure.",
  "profile.checkPassword": "Keep your password fresh",
  "profile.checkPasswordDone": "Last changed {date}.",
  "profile.checkPasswordTodo": "It is {days} days old — time to rotate it.",
  "profile.checkAction": "Fix",
  "profile.passwordAge": "Last changed",
  "profile.passwordExpires": "Expires",
  "profile.passwordNoPolicy": "Passwords never expire on this installation.",

  "profile.devices": "Signed-in devices",
  "profile.devicesHint": "Revoking a device ends its session immediately.",
  "profile.deviceUnknown": "Unknown device",
  "profile.sessionExpires": "Expires",
  "profile.mfaVerified": "MFA verified",
  "profile.revoked": "Session ended.",
  "profile.revokeConfirm": "End this session?",
  "profile.revokeDescription": "That device will have to sign in again.",
  "profile.revokeOthers": "Sign out other devices",
  "profile.revokeOthersConfirm": "Sign out every other device?",
  "profile.revokeOthersDescription": "Only this device stays signed in.",
  "profile.revokedOthers": "{count} sessions ended.",

  "profile.orgs": "Your organizations",
  "profile.orgsHint": "The tenants you can work in, and your role in each.",
  "profile.orgActive": "Selected",
  "profile.orgSwitch": "Switch to",
  "profile.orgsEmpty": "You are not a member of any organization yet.",

  "profile.activity": "Recent account activity",
  "profile.activityHint": "Events recorded against your account, newest first.",
  "profile.activityEmpty": "Nothing recorded yet.",

  "profile.mfaScanHint": "Scan this code with your authenticator app (Google Authenticator, 1Password, …).",
  "profile.mfaManualHint": "Can't scan? Add this key manually:",
  "profile.mfaCode": "6-digit code",

  "activityAction.auth.login.success": "Signed in",
  "activityAction.auth.login.failure": "Failed sign-in attempt",
  "activityAction.auth.logout": "Signed out",
  "activityAction.auth.password.changed": "Password changed",
  "activityAction.auth.password.reset_requested": "Password reset requested",
  "activityAction.auth.password.reset": "Password reset",
  "activityAction.auth.account.locked": "Account locked",
  "activityAction.auth.token.reuse_detected": "Session token reuse detected",
  "activityAction.mfa.enrolled": "Two-factor enabled",
  "activityAction.mfa.disabled": "Two-factor disabled",
  "activityAction.mfa.challenge.success": "Two-factor code accepted",
  "activityAction.mfa.challenge.failure": "Two-factor code rejected",
  "activityAction.session.revoked": "Session revoked",
  "activityAction.session.revoked_all": "All sessions revoked",
  "activityAction.user.updated": "Profile updated",
  "activityAction.user.email_verified": "Email verified",
  "activityAction.user.verification_requested": "Verification email requested",
  "activityAction.org.invite_accepted": "Invitation accepted",

  "dashboard.tileProducts": "Products",
  "dashboard.tilePublished": "Published passports",
  "dashboard.tilePassportsTotal": "Passports total",
  "dashboard.tileCarriers": "QR / data carriers",
  "dashboard.publicQrPages": "public QR pages",
  "dashboard.carriersNote": "issued",
  "dashboard.passportStatus": "Passport status",
  "dashboard.productStatus": "Product status",
  "dashboard.donutPassports": "passports",
  "dashboard.donutProducts": "products",
  "dashboard.issuance": "Monthly issuance",
  "dashboard.issuanceHint": "Published passports, last 12 months",
  "dashboard.usage": "Plan usage",
  "dashboard.viewsThisYear": "Public views this year",
  "dashboard.productAllowance": "Products",

  "users.title": "Users",
  "users.searchPlaceholder": "Search email or role…",
  "users.empty": "No users match",
  "users.active": "active",
  "users.disabled": "disabled",
  "users.enable": "Enable",
  "users.disable": "Disable",
  "users.delete": "Delete",
  "users.deleteConfirm": "Delete user?",
  "users.deleteWarning": "This permanently erases this account (GDPR):",
  "users.deleteConfirmAction": "Confirm delete",
  "users.updateFailed": "Failed to update user",
  "users.deleteFailed": "Delete failed",

  "audit.title": "Audit trail",
  "audit.allActions": "All actions",
  "audit.allActors": "All people",
  "audit.filterByAction": "Filter by action",
  "audit.filterByActor": "Filter by person",
  "audit.perPage": "/ page",
  "audit.empty": "No audit events",
  "audit.time": "Time",
  "audit.action": "Action",
  "audit.outcome": "Outcome",
  "audit.actor": "Actor",
  "audit.system": "System",
  "audit.success": "success",
  "audit.failure": "failure",
  "audit.today": "Today",
  "audit.last7": "Last 7 days",
  "audit.last30": "Last 30 days",

  "license.title": "License",
  "license.state": "State",
  "license.readOnly": "(read-only)",
  "license.customer": "Customer",
  "license.edition": "Edition",
  "license.expires": "Expires",
  "license.days": "days",
  "license.features": "Features",
  "license.seats": "Seats",
  "license.advanced": "Advanced",
  "license.fingerprint": "Fingerprint",
  "license.reasons": "Reasons",
  "license.installTitle": "Install / replace license",
  "license.installHint": "Paste the signed license token issued by your vendor.",
  "license.install": "Install license",
  "license.installed": "License installed.",
  "license.uploadFailed": "Upload failed",
} as const;

export type MessageKey = keyof typeof en;

const tr: Record<MessageKey, string> = {
  "nav.section.overview": "Genel Bakış",
  "nav.section.catalog": "Ürün & Pasaport",
  "nav.section.admin": "Yönetim",
  "nav.section.account": "Hesap",
  "nav.section.platform": "Platform",
  "platform.showDemo": "Demo verilerini göster",
  "platform.demoTag": "Demo",
  "platform.subtitleNoDemo": "Yalnızca gerçek müşteriler — {orgs} demo firma ve {users} demo hesap gizli.",
  "platform.subtitleWithDemo": "Demo firmalar ve demo hesaplar dahil.",
  "nav.platformOverview": "Özet",
  "nav.allProducts": "Tüm ürünler",
  "nav.allPassports": "Tüm pasaportlar",
  "platform.title": "Platform özeti",
  "platform.subtitle": "Bu kurulumdaki tüm firmalar, kullanıcılar ve pasaportlar.",
  "platform.note.activeOrgs": "{n} aktif",
  "platform.note.disabledUsers": "{n} devre dışı",
  "platform.note.views30d": "30 günde {n} pasaport görüntülenmesi",
  "platform.note.published": "{n} yayında",
  "platform.note.draft": "{n} taslak",
  "platform.attention": "Dikkat gerektirenler",
  "platform.allGood": "Şu an dikkat gerektiren bir şey yok.",
  "platform.views": "Pasaport görüntülenmeleri — son 30 gün",
  "platform.noViews": "Son 30 günde pasaport görüntülenmesi yok.",
  "platform.recentOrgs": "Son eklenen firmalar",
  "platform.seeAll": "Tümü",
  "platform.att.delinquent": "Ödemesi gecikmiş firmalar",
  "platform.att.viewLimit": "Görüntülenme hakkı dolan firmalar",
  "platform.att.suspended": "Askıdaki firmalar",
  "platform.att.orgsWithoutMembers": "Üyesi olmayan firmalar",
  "platform.att.usersWithoutOrg": "Firması olmayan kullanıcılar",
  "platform.att.unverified": "E-postası doğrulanmamış kullanıcılar",
  "platform.att.disabledUsers": "Devre dışı kullanıcılar",
  "platform.allOrgs": "Tüm firmalar",
  "platform.allProducts": "Tüm ürünler",
  "platform.searchProducts": "Ad, SKU veya GTIN ara…",
  "platform.noProducts": "Eşleşen ürün yok",
  "platform.product": "Ürün",
  "platform.allPassports": "Tüm pasaportlar",
  "platform.searchPassports": "Ürün, SKU veya genel bağlantı ara…",
  "platform.noPassports": "Eşleşen pasaport yok",
  "platform.version": "Sürüm",
  "platform.publicPage": "Genel sayfa",
  "platform.viaPlatformBanner":
    "{org} firmasında platform yöneticisi olarak çalışıyorsunuz (üye değilsiniz). Değişiklikler sizin adınıza kaydedilir.",
  "platform.backToConsole": "Konsola dön",
  "adminOrgs.planChanged": "{name}: paket güncellendi",
  "adminOrgs.flag.delinquent": "Gecikmiş fatura",
  "adminOrgs.flag.viewLimit": "Görüntülenme hakkı doldu",
  "adminOrgs.flag.noMembers": "Üyesi yok",
  "adminUsers.search": "E-posta veya ad ara…",
  "adminUsers.anyOrg": "Tüm firmalar",
  "adminUsers.withoutOrg": "Firması olmayanlar",
  "adminUsers.user": "Kullanıcı",
  "adminUsers.locked": "kilitli",
  "adminUsers.lastActive": "Son etkinlik",
  "adminUsers.passwordSet": "Şifre güncellendi; kullanıcının tüm oturumları kapatıldı",
  "adminUsers.unlock": "Kilidi aç",
  "adminUsers.unlocked": "Kilit kaldırıldı",
  "adminUsers.setPassword": "Şifre belirle",
  "adminUsers.passwordSetHandOver":
    "Yeni şifre — güvenli bir kanalla iletin; bir daha gösterilmez. Kullanıcıya şifresinin değiştiği e-postayla bildirildi.",
  "adminUsers.setPasswordHelp":
    "Destek durumları için (şifre unutuldu, sıfırlama e-postası gelmiyor). Kullanıcının tüm oturumları kapatılır ve e-postayla bilgilendirilir.",
  "adminUsers.newPassword": "Yeni şifre",
  "nav.adminOrgs": "Firmalar",
  "adminOrgs.title": "Tüm firmalar",
  "adminOrgs.create": "Yeni firma",
  "adminOrgs.summary": "{orgs} firma · {products} ürün · {passports} pasaportun {published} tanesi yayında",
  "adminOrgs.search": "Ad veya kısa ad ara…",
  "adminOrgs.allStatuses": "Tüm durumlar",
  "adminOrgs.empty": "Eşleşen firma yok",
  "adminOrgs.name": "Firma",
  "adminOrgs.members": "Üye",
  "adminOrgs.plan.free": "Ücretsiz",
  "adminOrgs.plan.unlimited": "Limitsiz",
  "adminOrgs.plan.none": "Faturalanmıyor",
  "adminOrgs.manage": "Üyeler & ayarlar",
  "adminOrgs.status.active": "Aktif",
  "adminOrgs.status.suspended": "Askıda",
  "adminOrgs.status.wound_down": "Tasfiye",
  "adminOrgs.statusConfirmTitle": "Firma durumunu değiştir",
  "adminOrgs.statusConfirmBody": "{name} durumu şu olsun mu: {status}?",
  "adminOrgs.statusReason": "Gerekçe (denetim kaydına yazılır)",
  "adminOrgs.statusChanged": "{name}: durum güncellendi",
  "adminUsers.create": "Yeni kullanıcı",
  "adminUsers.created": "Kullanıcı oluşturuldu.",
  "adminUsers.handOver":
    "Bu bilgileri güvenli bir kanalla iletin; şifre bir daha gösterilmez. Kullanıcı Profil sayfasından değiştirebilir.",
  "adminUsers.displayName": "Ad",
  "adminUsers.password": "İlk şifre",
  "adminUsers.generate": "Üret",
  "adminUsers.platformRole": "Platform rolü",
  "adminUsers.platformRoleHelp":
    "Platform rolleri tüm firmalarda geçerlidir (ör. admin, auditor). Müşterilerin genelde ihtiyacı yoktur — onlara firma rolü verin.",
  "adminUsers.noPlatformRole": "Yok (sadece firma rolleri)",
  "adminUsers.firstOrg": "Firmaya ekle",
  "adminUsers.noOrg": "Şimdilik ekleme",
  "adminUsers.orgAddFailed": "Kullanıcı oluşturuldu ama firmaya eklenemedi",
  "adminUsers.emailVerified": "e-posta doğrulandı",
  "adminUsers.emailUnverified": "e-posta doğrulanmadı",
  "adminUsers.revokeSessions": "Tüm oturumları kapat",
  "adminUsers.sessionsRevoked": "Tüm oturumlar kapatıldı",
  "adminUsers.enabled": "Kullanıcı etkinleştirildi",
  "adminUsers.disabledMsg": "Kullanıcı devre dışı bırakıldı",
  "adminUsers.platformRoles": "Platform rolleri",
  "adminUsers.pickRole": "Rol seçin…",
  "adminUsers.assignRole": "Ata",
  "adminUsers.roleAssigned": "{role} rolü atandı",
  "adminUsers.roleRevoked": "{role} rolü kaldırıldı",
  "adminUsers.orgs": "Firma üyelikleri",
  "adminUsers.noOrgs": "Hiçbir firmaya üye değil",
  "adminUsers.pickOrg": "Firma seçin…",
  "adminUsers.addToOrg": "Ekle",
  "adminUsers.orgAdded": "{org} firmasına eklendi",
  "adminUsers.orgRemoved": "{org} firmasından çıkarıldı",
  "adminUsers.orgRoleChanged": "{org} içindeki rol güncellendi",
  "nav.dashboard": "Panel",
  "nav.users": "Kullanıcılar",
  "nav.audit": "Denetim",
  "nav.license": "Lisans",
  "nav.uiKit": "UI Kiti",
  "nav.products": "Ürünler",
  "nav.passports": "Pasaportlar",
  "nav.import": "İçe aktar",
  "nav.org": "Organizasyon",
  "nav.profile": "Profil",

  "common.save": "Kaydet",
  "common.cancel": "İptal",
  "common.close": "Kapat",
  "common.create": "Oluştur",
  "common.actions": "İşlemler",
  "common.status": "Durum",
  "common.createdAt": "Oluşturulma",
  "common.updatedAt": "Güncellenme",
  "common.loading": "Yükleniyor…",
  "common.view": "Görünüm",
  "common.viewList": "Liste görünümü",
  "common.viewGrid": "Kart görünümü",
  "common.totalCount": "Toplam {count}",
  "common.noSearchResults": "Filtrelerinizle eşleşen kayıt yok",
  "common.clearFilters": "Filtreleri temizle",
  "common.yes": "Evet",
  "common.no": "Hayır",
  "common.remove": "Kaldır",
  "common.moveUp": "Yukarı taşı",
  "common.moveDown": "Aşağı taşı",
  "common.archive": "Arşivle",
  "common.signOut": "Çıkış yap",
  "common.openNav": "Menüyü aç",
  "common.toggleTheme": "Temayı değiştir",
  "common.menu": "Menü",
  "common.language": "Dil",
  "common.email": "E-posta",
  "common.roles": "Roller",
  "common.mfa": "MFA",
  "common.ip": "IP",

  "error.loadFailed": "Veriler yüklenemedi",
  "error.saveFailed": "Kaydedilemedi",
  "error.actionFailed": "İşlem başarısız oldu",

  "demo.banner":
    "Demo hesabı — tüm formları inceleyebilirsiniz, ancak değişiklikler kaydedilmez. Public pasaport sayfaları açık kalır.",
  "demo.writeBlocked": "Bu bir demo hesabıdır — değişiklikler kaydedilemez.",

  "org.switcher": "Organizasyon",
  "org.search": "Organizasyon ara…",
  "org.create": "Organizasyon oluştur",
  "org.createTitle": "Yeni organizasyon",
  "org.name": "Ad",
  "org.slug": "Kısa ad (slug)",
  "org.slugHelp": "Küçük harf, rakam ve tire (URL'lerde kullanılır).",
  "org.gs1Prefix": "GS1 firma öneki",
  "org.gs1PrefixHelp": "GS1 tarafından atanan 4–12 hane (isteğe bağlı).",
  "org.plan": "Plan",
  "org.addSelfOwner": "Beni ilk sahip olarak ekle",
  "org.asSubOrg": "Şunun alt organizasyonu olarak oluştur:",
  "billingPlan.free": "Ücretsiz (1000 görüntülenme/yıl, 10 ürün)",
  "billingPlan.unlimited": "Limitsiz (1000 $/yıl, sınırsız görüntülenme ve ürün)",
  "billingPlan.none": "Faturalanmıyor",
  "org.created": "Organizasyon oluşturuldu",
  "org.empty": "Henüz hiçbir organizasyonun üyesi değilsiniz.",
  "org.emptyHint": "Bir platform yöneticisinden sizi bir organizasyona eklemesini isteyin.",
  "org.emptyTitle": "İlk organizasyonunu oluştur",
  "org.emptyCtaHint":
    "Ürünlerinizi ve dijital ürün pasaportlarını yönetmeye başlamak için bir organizasyon kurun.",

  "products.title": "Ürünler",
  "products.new": "Yeni ürün",
  "products.createTitle": "Yeni ürün",
  "products.created": "Ürün oluşturuldu",
  "products.searchPlaceholder": "SKU veya ad ara…",
  "products.empty": "Henüz ürün yok",
  "products.sku": "SKU",
  "products.name": "Ad",
  "products.gtin": "GTIN",
  "products.gtinHelp": "8, 12, 13 veya 14 hane (isteğe bağlı).",
  "products.commodityCode": "Emtia kodu (CN)",
  "products.commodityCodeHelp": "8-10 hane — AB gümrük CN/TARIC kodu, DPP Registry'ye gönderilir.",
  "products.gtip": "GTİP (TR gümrük)",
  "products.gtipHelp":
    "12 hane — Gümrük Tarife İstatistik Pozisyonu; ilk 8 hanesi CN koduyla aynı olmalıdır.",
  "products.gtipMismatch": "GTİP'in ilk 8 hanesi CN emtia koduyla eşleşmiyor.",
  "products.schema": "Pasaport şablonu",

  "productStatus.draft": "taslak",
  "productStatus.active": "aktif",
  "productStatus.archived": "arşivli",

  "product.notFound": "Ürün bulunamadı",
  "product.unknownSchema": "Bilinmeyen pasaport şablonu — bu ürün burada düzenlenemez.",
  "product.compliance": "Uyum",
  "product.complete": "Tüm zorunlu alanlar dolu.",
  "product.missingRequired": "Eksik zorunlu alanlar",
  "product.saved": "Ürün kaydedildi",
  "product.addRow": "Satır ekle",
  "product.data": "Ürün verileri",
  "product.missingShort": "eksik",
  "product.noRows": "Henüz satır yok — ilkini ekleyin.",
  "product.editedCount": "alan değiştirildi",
  "product.discard": "Vazgeç",

  "common.edit": "Düzenle",
  "nav.facilities": "Tesisler",
  "nav.disclosures": "İmha beyanı",

  "facilities.title": "Tesisler ve Sertifikalar",
  "facilities.facilities": "Tesisler",
  "facilities.new": "Yeni tesis",
  "facilities.edit": "Tesisi düzenle",
  "facilities.saved": "Tesis kaydedildi",
  "facilities.deleted": "Tesis kaldırıldı",
  "facilities.empty": "Henüz tesis yok — kendi tesislerinizi ve tedarikçi tesislerini ekleyin.",
  "facilities.name": "Ad",
  "facilities.location": "Konum",
  "facilities.country": "Ülke",
  "facilities.countryPlaceholder": "Ülke ara…",
  "facilities.city": "Şehir",
  "facilities.employees": "Çalışan sayısı",
  "facilities.renewable": "Yenilenebilir enerji",
  "facilities.lat": "Enlem",
  "facilities.lng": "Boylam",
  "facilities.waterRecycling": "Su geri kazanımı",
  "facilities.wastewaterTreatment": "Atık su arıtma",
  "facilities.practices": "Üretim uygulamaları",
  "facilities.certifications": "Sertifikalar",
  "facilities.mapTitle": "Tesis konumları",
  "facilities.identifier": "Tanımlayıcı",
  "facilities.identifierScheme": "Tanımlayıcı şeması",
  "facilities.identifierNone": "Yok (platform URN'i)",
  "facilities.identifierInternal": "Dahili kod",
  "facilities.identifierValue": "Tanımlayıcı değeri",
  "facilities.identifierHelp":
    "GLN: geçerli kontrol haneli 13 hane. Yoksa pasaport bu tesis için sabit bir platform URN'i yayımlar.",

  "map.search": "Ara",
  "map.searchPlaceholder": "Adres veya yer arayın…",
  "map.noResult": "Bu arama için konum bulunamadı.",
  "map.unavailable": "Harita yapılandırılmamış — koordinatları elle girin.",
  "map.pickHint": "Tam konumu belirlemek için haritaya tıklayın veya işaretçiyi sürükleyin.",

  "certs.title": "Sertifikalar",
  "certs.new": "Yeni sertifika",
  "certs.edit": "Sertifikayı düzenle",
  "certs.saved": "Sertifika kaydedildi",
  "certs.deleted": "Sertifika kaldırıldı",
  "certs.empty": "Henüz sertifika yok — GOTS, OEKO-TEX, ISO gibi sertifikaları kaydedin.",
  "certs.scheme": "Şema",
  "certs.no": "Sertifika no",
  "certs.issuer": "Veren kuruluş",
  "certs.validFrom": "Geçerlilik başlangıcı",
  "certs.validUntil": "Geçerlilik sonu",
  "certs.evidenceUrl": "Kanıt bağlantısı",
  "certs.noExpiry": "süresiz",
  "certs.image": "Sertifika görseli",
  "certs.imageUpload": "Görsel yükle",
  "certs.imageReplace": "Görseli değiştir",
  "certs.imageRemove": "Görseli kaldır",
  "certs.imageHint":
    "JPEG, PNG veya WebP, en fazla 5 MB. Halka açık pasaportta sertifikanın yanında gösterilir.",
  "certs.imagePending": "Görsel, sertifika kaydedildiğinde yüklenecek.",
  "certs.imageUploaded": "Sertifika görseli yüklendi",
  "certs.imageDeleted": "Sertifika görseli kaldırıldı",
  "certs.imageFailed": "Sertifika kaydedildi ancak görseli yüklenemedi.",

  "chain.title": "Tedarik zinciri",
  "chain.hint":
    "Aşamaları ham maddeden sevkiyata doğru sıralayın. Kısıtlı aşamalar yalnızca erişim izni sahiplerine görünür.",
  "chain.empty": "Henüz aşama yok.",
  "chain.saved": "Tedarik zinciri kaydedildi",
  "chain.save": "Tedarik zincirini kaydet",
  "chain.addStage": "Aşama ekle",
  "chain.transport": "Taşıma yöntemi",
  "chain.notes": "Notlar",
  "chain.noFacility": "Tesis yok",
  "chain.productCerts": "Ürün sertifikaları",
  "chain.stage": "Aşama",
  "chain.facility": "Tesis",
  "chain.stagesTitle": "Üretim aşamaları",
  "chain.stageCount": "{n} aşama",
  "chain.mapTitle": "Üretim rotası",
  "chain.certsHint": "Bitmiş ürüne uygulanan sertifikalar.",
  "chain.partnersHint": "Bu ürün için sunulan geri alım ve döngüsellik partnerleri.",
  "chain.unsaved": "Zinciri gözden geçirin, ardından değişiklikleri kaydedin.",

  "comps.title": "Bileşenler",
  "comps.hint": "Bileşen düzeyinde izlenebilirlik: ürünü oluşturan parçaları kaynağıyla birlikte listeleyin.",
  "comps.empty": "Henüz bileşen yok.",
  "comps.saved": "Bileşenler kaydedildi",
  "comps.save": "Bileşenleri kaydet",
  "comps.add": "Bileşen ekle",
  "comps.addChild": "Alt parça ekle",
  "comps.name": "Bileşen adı",
  "comps.type": "Tür",
  "comps.weight": "Ağırlık",
  "comps.image": "Görsel",
  "comps.imageSet": "Görsel ✓",
  "comps.materialNote": "Malzeme / not",
  "comps.separable": "Ayrılabilir parça",
  "comps.facility": "Tesis",
  "comps.replaceImage": "Değiştir",
  "comps.removeImage": "Görseli kaldır",
  "comps.rootTitle": "Malzeme listesi",
  "comps.compCount": "{n} parça",

  "dashboard.expiringCerts": "90 gün içinde süresi dolacak sertifikalar:",
  "dashboard.expiringCertsLink": "sertifikaları yönet",

  "nav.reviews": "Yorumlar",
  "lca.title": "LCA",
  "lca.hint":
    "Metodolojisiyle birlikte yaşam döngüsü değerlendirmesi sonucu — public pasaportta kaynak ve veri seti beyanlarıyla gösterilir.",
  "lca.saved": "LCA sonucu kaydedildi",
  "lca.deleted": "LCA sonucu kaldırıldı",
  "lca.delete": "LCA'yı kaldır",
  "lca.source": "Kaynak",
  "lca.sourceManual": "Manuel beyan",
  "lca.sourceEpd": "EPD referansı",
  "lca.sourceClca": "clca.app motoru",
  "lca.externalRef": "Dış referans",
  "lca.method": "Metot",
  "lca.functionalUnit": "Fonksiyonel birim",
  "lca.systemBoundary": "Sistem sınırı",
  "lca.dataset": "Veri seti",
  "lca.calculatedAt": "Hesap tarihi",
  "lca.verifiedBy": "Doğrulayan",
  "lca.indicators": "Göstergeler",
  "lca.addIndicator": "Gösterge ekle",
  "lca.indicator": "Gösterge",
  "lca.value": "Değer",
  "lca.unit": "Birim",
  "lca.module": "Modül",

  "lca.methodology": "Yöntem ve kaynak",
  "lca.methodologyHint":
    "Bu beyanlar sayıların yanında yayımlanır — yöntemi, sınırı ve veri seti belirtilmemiş bir sonuç doğrulanabilir değildir.",
  "lca.externalRefPh": "EPD kayıt no veya URL",
  "lca.externalRefHelp": "EPD / doğrulama raporunun kayıt numarası ya da herkese açık adresi.",
  "lca.methodPh": "örn. EN 15804+A2",
  "lca.functionalUnitPh": "örn. 1 adet ürün",
  "lca.systemBoundaryPh": "örn. beşikten kapıya",
  "lca.datasetPh": "örn. ecoinvent 3.11",
  "lca.verifiedByPh": "Doğrulayan kuruluş veya program",
  "lca.indicatorsHint":
    "Beyan edilen her gösterge bir satır. Katalogdan seçtiğinizde EN 15804 birimi otomatik gelir.",
  "lca.indicatorPh": "Kod arayın veya yazın",
  "lca.indicatorPick": "Gösterge ekle…",
  "lca.indicatorCustom": '"{q}" kodunu özel gösterge olarak ekle',
  "lca.addCoreSet": "EN 15804+A2 çekirdek seti",
  "lca.addHeadlineSet": "Karbon · enerji · su",
  "lca.indicatorsEmpty": "Henüz gösterge beyan edilmedi",
  "lca.indicatorsEmptyHint":
    "Hızlı beyan için üç temel metriği, EPD seviyesinde bir profil için tüm çekirdek seti ekleyin.",
  "lca.count": "{n} / {max} gösterge",
  "lca.incomplete": "{n} satırda değer veya birim yok — bu satırlar kaydedilmeyecek.",
  "lca.rowIncomplete": "Değer ve birim zorunlu — bu satır kaydedilmeyecek.",
  "lca.duplicate": "Bu kod ve modül ikilisi iki kez beyan edilmiş.",
  "lca.unitMismatch": "Bu göstergenin EN 15804 birimi: {unit}",
  "lca.alreadyAdded": "Zaten eklendi",
  "lca.maxReached": "En fazla 40 gösterge beyan edilebilir.",
  "lca.unsaved": "Kaydedilmemiş değişiklikler var",
  "lca.publishCount": "{n} gösterge yayımlanacak",
  "lca.deleteConfirm": "LCA sonucu kaldırılsın mı?",
  "lca.deleteConfirmDesc": "Beyan ve tüm göstergeler üründen silinir.",

  "lcaHead.carbon": "Karbon ayak izi",
  "lcaHead.energy": "Enerji (yenilenemez)",
  "lcaHead.water": "Su kullanımı",

  "lcaGroup.impact": "Çevresel etki",
  "lcaGroup.resource": "Kaynak kullanımı",
  "lcaGroup.waste": "Atık ve çıktı akışları",

  "lcaInd.GWP_total": "Küresel ısınma potansiyeli — toplam",
  "lcaInd.GWP_fossil": "Küresel ısınma — fosil",
  "lcaInd.GWP_biogenic": "Küresel ısınma — biyojenik",
  "lcaInd.GWP_luluc": "Küresel ısınma — arazi kullanımı değişimi",
  "lcaInd.ODP": "Ozon tabakası incelmesi",
  "lcaInd.AP": "Asitleşme",
  "lcaInd.EP_freshwater": "Ötrofikasyon — tatlı su",
  "lcaInd.EP_marine": "Ötrofikasyon — deniz",
  "lcaInd.EP_terrestrial": "Ötrofikasyon — karasal",
  "lcaInd.POCP": "Fotokimyasal ozon oluşumu",
  "lcaInd.ADPE": "Kaynak kullanımı — mineral ve metal",
  "lcaInd.ADPF": "Kaynak kullanımı — fosil yakıt",
  "lcaInd.WDP": "Su kullanımı (yoksunluk ağırlıklı)",
  "lcaInd.PERE": "Yenilenebilir birincil enerji — enerji olarak",
  "lcaInd.PERM": "Yenilenebilir birincil enerji — hammadde olarak",
  "lcaInd.PERT": "Yenilenebilir birincil enerji — toplam",
  "lcaInd.PENRE": "Yenilenemez birincil enerji — enerji olarak",
  "lcaInd.PENRM": "Yenilenemez birincil enerji — hammadde olarak",
  "lcaInd.PENRT": "Yenilenemez birincil enerji — toplam",
  "lcaInd.SM": "İkincil malzeme",
  "lcaInd.RSF": "Yenilenebilir ikincil yakıt",
  "lcaInd.NRSF": "Yenilenemez ikincil yakıt",
  "lcaInd.FW": "Net tatlı su kullanımı",
  "lcaInd.HWD": "Bertaraf edilen tehlikeli atık",
  "lcaInd.NHWD": "Bertaraf edilen tehlikesiz atık",
  "lcaInd.RWD": "Bertaraf edilen radyoaktif atık",
  "lcaInd.CRU": "Yeniden kullanıma giden bileşen",
  "lcaInd.MFR": "Geri dönüşüme giden malzeme",
  "lcaInd.MER": "Enerji geri kazanımına giden malzeme",
  "lcaInd.EEE": "Dışa verilen elektrik enerjisi",
  "lcaInd.EET": "Dışa verilen ısı enerjisi",

  "lcaModGroup.product": "Ürün aşaması (A1–A3)",
  "lcaModGroup.construction": "Kurulum (A4–A5)",
  "lcaModGroup.use": "Kullanım aşaması (B1–B7)",
  "lcaModGroup.endOfLife": "Ömür sonu (C1–C4)",
  "lcaModGroup.beyond": "Sınır ötesi (D)",

  "lcaMod.A1": "Ham madde temini",
  "lcaMod.A2": "Taşıma",
  "lcaMod.A3": "Üretim",
  "lcaMod.A1-A3": "Ürün aşaması",
  "lcaMod.A4": "Sahaya taşıma",
  "lcaMod.A5": "Kurulum",
  "lcaMod.B1": "Kullanım",
  "lcaMod.B2": "Bakım",
  "lcaMod.B3": "Onarım",
  "lcaMod.B4": "Değişim",
  "lcaMod.B5": "Yenileme",
  "lcaMod.B6": "İşletme enerjisi",
  "lcaMod.B7": "İşletme suyu",
  "lcaMod.C1": "Sökme",
  "lcaMod.C2": "Atık işlemeye taşıma",
  "lcaMod.C3": "Atık işleme",
  "lcaMod.C4": "Bertaraf",
  "lcaMod.C1-C4": "Ömür sonu",
  "lcaMod.D": "Sınır ötesi fayda ve yükler",

  "lcaBoundary.cradle_to_gate": "Beşikten kapıya (A1–A3)",
  "lcaBoundary.cradle_to_gate_with_options": "Beşikten kapıya + seçenekler (A1–A3 + C, D)",
  "lcaBoundary.cradle_to_grave": "Beşikten mezara (A–C)",
  "lcaBoundary.cradle_to_cradle": "Beşikten beşiğe",
  "lcaBoundary.gate_to_gate": "Kapıdan kapıya",

  "lcaFu.piece": "1 adet ürün",
  "lcaFu.kg": "1 kg ürün",
  "lcaFu.m2": "1 m² kumaş",
  "lcaFu.m": "1 m",
  "lcaFu.l": "1 L",
  "lcaFu.pair": "1 çift",
  "lcaFu.k1000": "1000 adet",

  "partners.title": "Döngüsellik partnerleri",
  "partners.new": "Yeni partner",
  "partners.edit": "Partneri düzenle",
  "partners.saved": "Partner kaydedildi",
  "partners.deleted": "Partner kaldırıldı",
  "partners.empty": "Henüz partner yok — bağış, onarım, geri dönüşüm ve ikinci el noktaları ekleyin.",
  "partners.category": "Kategori",
  "partnerCat.donation": "Bağış",
  "partnerCat.repair": "Onarım",
  "partnerCat.upcycle": "İleri dönüşüm",
  "partnerCat.recycle": "Geri dönüşüm",
  "partnerCat.resale": "İkinci el",
  "partners.categoryHint": "Bir kategori seçin ya da yazarak firmanıza özel yeni bir kategori oluşturun.",
  "partners.categorySearch": "Arayın veya yeni kategori yazın…",
  "partners.categoryCreate": "\u201c{name}\u201d kategorisini oluştur",
  "partners.categoryCustom": "Kendi kategorileriniz",
  "chain.productPartners": "Bu ürünün döngüsellik partnerleri",

  "reviews.title": "Müşteri yorumları",
  "reviews.empty": "Yorum yok.",
  "reviews.product": "Ürün",
  "reviews.author": "Yazan",
  "reviews.anonymous": "Anonim",
  "reviews.rating": "Puan",
  "reviews.body": "Yorum",
  "reviews.approve": "Onayla",
  "reviews.reject": "Reddet",
  "reviews.approvedToast": "Yorum onaylandı",
  "reviews.rejectedToast": "Yorum reddedildi",
  "reviews.statusPending": "Bekleyen",
  "reviews.statusApproved": "Onaylı",
  "reviews.statusRejected": "Reddedilen",
  "reviews.statusAll": "Tümü",
  "reviews.status_pending": "Bekliyor",
  "reviews.status_approved": "Onaylı",
  "reviews.status_rejected": "Reddedildi",
  "reviews.subtitle":
    "Halka açık pasaport sayfasından gönderilen yorumlar burada bekler — siz onaylamadan hiçbiri yayımlanmaz.",
  "reviews.searchPlaceholder": "Ürün, yazan veya metin ara",
  "reviews.avgRating": "Ortalama puan",
  "reviews.avgRatingNote": "Yalnızca onaylı yorumlar",
  "reviews.noBody": "Yorum metni yok.",
  "reviews.emptyPending": "Bekleyen yorum yok — hepsi güncel.",
  "reviews.emptyFiltered": "Aramayla eşleşen yorum yok.",
  "reviews.rejectConfirm": "Bu yorum reddedilsin mi?",
  "reviews.retract": "Yayından kaldır",
  "reviews.retractConfirm": "Bu yorum halka açık sayfadan kaldırılsın mı?",

  "dashboard.scans": "Pasaport sayfası görüntülenmeleri",
  "dashboard.scansHint": "Son 30 günün günlük public pasaport görüntülenmeleri (ziyaretçi verisi saklanmaz).",
  "theme.title": "Public sayfa teması",
  "theme.hint":
    "Public pasaport sayfalarını markalayın: vurgu rengi, gizlenen bölümler ve özel bölüm adları.",
  "theme.saved": "Tema kaydedildi",
  "theme.accent": "Vurgu rengi",
  "theme.sections": "Bölümler (gizlemek için kapatın, yeniden adlandırmak için yazın)",
  "theme.renamePlaceholder": "Özel ad",
  "theme.hidden": "Gizli",
  "theme.sectionsVisible": "bölüm görünür",
  "theme.reset": "Sıfırla",
  "themeSection.highlights": "Öne çıkanlar",
  "themeSection.operators": "İşletmeci bilgileri",
  "themeSection.documents": "Belgeler ve talimatlar",
  "themeSection.identity": "Ürün kimliği",
  "themeSection.materials": "Malzemeler",
  "themeSection.circularity": "Dayanıklılık ve döngüsellik",
  "themeSection.use": "Bakım ve ömür sonu",
  "themeSection.supply-chain": "Tedarik zinciri",
  "themeSection.components": "Bileşenler",
  "themeSection.lca": "LCA",
  "themeSection.circularity-partners": "Döngüsellik partnerleri",
  "themeSection.certifications": "Sertifikalar",
  "themeSection.reviews": "Yorumlar",

  "tier.public": "Herkese açık",
  "tier.restricted": "Kısıtlı",
  "tier.label": "Görünürlük",
  "stage.design": "Tasarım",
  "stage.raw_material": "Ham madde",
  "stage.spinning": "İplik",
  "stage.weaving": "Dokuma",
  "stage.dyeing": "Boyama ve terbiye",
  "stage.sampling": "Numune",
  "stage.trims": "Aksesuarlar",
  "stage.manufacturing": "Üretim",
  "stage.delivery": "Sevkiyat",

  "media.title": "Görseller",
  "media.upload": "Görsel yükle",
  "media.uploaded": "Görsel yüklendi",
  "media.deleted": "Görsel kaldırıldı",
  "media.empty": "Henüz görsel yok — herkese açık pasaport galerisi için ürün fotoğrafları yükleyin.",
  "media.tooLarge": "Görsel 5 MB sınırını aşıyor",
  "media.hint": "JPEG, PNG veya WebP; ürün başına en fazla 5 MB ve 10 görsel.",

  "documents.title": "Belgeler",
  "documents.hint":
    "Kullanım talimatı, güvenlik bilgisi, AB uygunluk beyanı ve sertifikalar pasaportla BİRLİKTE gider (PDF, en fazla 20 MB, ürün başına 20). Yayımlama güncel seti dondurur; talimatlar ürünün ömrü boyunca çevrimiçi kalır. Hiçbir şey silinmez — yeni sürüm yükleyin ya da geri çekin.",
  "documents.kind": "Tür",
  "documents.titleLabel": "Başlık",
  "documents.titleRequired": "Önce belgeye bir başlık verin.",
  "documents.lang": "Dil",
  "documents.tier": "Görünürlük",
  "documents.size": "Boyut",
  "documents.referenceOnly": "referans",
  "documents.upload": "PDF yükle",
  "documents.tooLarge": "Belge 20 MB sınırını aşıyor",
  "documents.uploaded": "Belge kaydedildi",
  "documents.withdrawn": "Belge geri çekildi",
  "documents.withdrawConfirm": "Bu belge geri çekilsin mi?",
  "documents.withdrawDescription":
    "Yayımlanmış pasaportlar belgeyi sunmaya devam eder; bir sonraki yayında belge yer almaz. Geri alınamaz.",
  "documents.open": "Aç",
  "documents.empty": "Henüz belge yok — en azından kullanım talimatını ekleyin.",
  "documents.addReference": "Teknik dosya referansı ekle",
  "documents.referenceHint":
    "Teknik dosyanın kendisi gizlidir ve yüklenmez — nerede tutulduğunu kaydedin (kısıtlı katman).",
  "documents.referenceNote": "Dosyanın tutulduğu yer",
  "documents.referenceUrl": "Bağlantı (isteğe bağlı)",
  "documents.status.current": "güncel",
  "documents.status.superseded": "yenilendi",
  "documents.status.withdrawn": "geri çekildi",

  "documentKind.instructions": "Kullanım talimatı",
  "documentKind.safety_information": "Güvenlik bilgisi",
  "documentKind.declaration_of_conformity": "AB uygunluk beyanı",
  "documentKind.test_report": "Test raporu",
  "documentKind.certificate": "Sertifika",
  "documentKind.authorised_rep_mandate": "Yetkili temsilci talimatı",
  "documentKind.technical_file_reference": "Teknik dosya referansı",
  "documentKind.other": "Diğer",

  "readiness.title": "Hazırlık kontrolleri",
  "readiness.block": "yayını engeller",
  "readiness.warn": "uyarı",
  "product.readOnly": "Ürün düzenleme yetkiniz yok — form salt okunur.",
  "product.identifiers": "Tanımlayıcılar",
  "product.assignIdentifier": "Tanımlayıcı ata",
  "product.identifierAssigned": "Tanımlayıcı atandı",
  "product.scheme": "Şema",
  "product.granularity": "Ayrıntı düzeyi",
  "product.serial": "Seri no",
  "product.serialHelp": "Ürün (item) düzeyindeki GS1 tanımlayıcılar için zorunludur.",
  "product.identifierValue": "Değer",
  "product.noIdentifiers": "Atanmış tanımlayıcı yok",
  "product.lot": "Parti / lot",
  "product.lotHelp":
    "Parti düzeyindeki GS1 tanımlayıcılar için zorunludur (AI 10); ürün düzeyinde isteğe bağlıdır.",
  "product.identifierStatus": "Durum",
  "product.withdrawIdentifier": "Geri çek",
  "product.withdrawConfirm": "Bu tanımlayıcı geri çekilsin mi?",
  "product.withdrawDescription":
    "QR kod çözülmeye devam eder; ancak pasaport sayfası tanımlayıcıyı geri çekilmiş olarak işaretler ve yeni pasaport bu tanımlayıcıya bağlanamaz. Geri alınamaz.",
  "product.identifierWithdrawn": "Tanımlayıcı geri çekildi",

  "identifierStatus.active": "Aktif",
  "identifierStatus.withdrawn": "Geri çekildi",

  "idScheme.gs1": "GS1 Digital Link",
  "idScheme.internal": "Dahili",
  "idGranularity.model": "model",
  "idGranularity.batch": "parti",
  "idGranularity.item": "ürün",

  "import.title": "CSV içe aktarma",
  "import.hint":
    "Beklenen sütunlar: sku, name, gtin (isteğe bağlı), gtip (isteğe bağlı, 12 haneli GTİP; commodityCode da kabul edilir), schemaId — artı alan kimliğiyle adreslenen öznitelik sütunları (örn. identity.brand). Satırlar SKU'ya göre eklenir/güncellenir.",
  "import.pasteLabel": "CSV yapıştır",
  "import.fileLabel": "…veya bir .csv dosyası ya da IDPP Excel veri şablonu (.xlsx) yükleyin",
  "import.xlsxLoaded": "Excel dosyasından {n} ürün sayfası okundu — kontrol edip içe aktarın",
  "import.xlsxEmpty": "Excel dosyasında doldurulmuş ürün sayfası bulunamadı",
  "import.sheet": "Sayfa",
  "import.run": "İçe aktar",
  "import.created": "Oluşturulan",
  "import.updated": "Güncellenen",
  "import.errorCount": "Hata",
  "import.errorsTitle": "Satır hataları",
  "import.row": "Satır",
  "import.message": "Mesaj",
  "import.done": "İçe aktarma tamamlandı",
  "products.excelImport": "Excel'den veri aktar",
  "excelImport.title": "Excel'den veri aktar",
  "excelImport.steps":
    "1. Pasaport türünüzü seçip Excel şablonunu indirin. 2. Her ürün için bir sayfa doldurun (daha fazla ürün için 'Ürün 1' sayfasını kopyalayın). 3. Dosyayı yükleyin, verileri kontrol edip tamamlayın ve kaydedin.",
  "excelImport.template": "Pasaport şablonu",
  "excelImport.download": "Excel şablonunu indir",
  "excelImport.upload": "Doldurduğunuz Excel dosyasını yükleyin (.xlsx)",
  "excelImport.toFix": "{n} kontrol edilecek",
  "excelImport.ready": "{n} kaydedilmeye hazır",
  "excelImport.fixHint": "İşaretli alanları tamamlayın; yalnızca eksiksiz ürünler kaydedilir.",
  "excelImport.allReady": "Eksik bilgi yok, tüm ürünler kaydedilmeye hazır.",
  "excelImport.updateNote": "SKU'su zaten kayıtlı olan ürünler güncellenir.",
  "excelImport.status.ready": "Hazır",
  "excelImport.status.issues": "{n} alan eksik/hatalı",
  "excelImport.status.fault": "Sunucu reddetti",
  "excelImport.required": "Zorunlu alan",
  "excelImport.invalid.number": "Geçerli bir sayı girin",
  "excelImport.invalid.percentage": "0 ile 100 arasında bir sayı girin",
  "excelImport.invalid.enum": "Listeden bir değer seçin",
  "excelImport.invalid.date": "Geçerli bir tarih girin",
  "excelImport.invalid.url": "http:// veya https:// ile başlayan bir bağlantı girin",
  "excelImport.invalid.boolean": "Evet ya da Hayır seçin",
  "excelImport.invalid.text": "Geçersiz değer",
  "excelImport.sku": "Ürün kodu (SKU)",
  "excelImport.name": "Ürün adı",
  "excelImport.skuInvalid": "En fazla 64 karakter olmalı ve dosyada tekrar etmemeli",
  "excelImport.nameInvalid": "En fazla 200 karakter",
  "excelImport.unknownSchema": "Bilinmeyen pasaport şablonu: {id}",
  "excelImport.remove": "Listeden çıkar",
  "excelImport.back": "Başka dosya yükle",
  "excelImport.save": "Tümünü kaydet ({n})",
  "excelImport.saved": "{n} ürün kaydedildi",
  "excelImport.rejected": "{n} ürün sunucu tarafından reddedildi; düzeltip yeniden kaydedin",
  "excelImport.orphans":
    "{n} liste satırının ürün kodu (SKU) hiçbir ürün sayfasıyla eşleşmedi, bu satırlar aktarılmayacak: {rows}. Liste sayfalarındaki SKU sütununu kontrol edin.",

  "suppliers.title": "Tedarikçiler",
  "suppliers.invite": "Tedarikçi davet et",
  "suppliers.selectAll": "Tümünü seç",
  "suppliers.clear": "Temizle",
  "suppliers.inviteTitle": "Yeni tedarikçi daveti",
  "suppliers.name": "Tedarikçi adı",
  "suppliers.email": "Tedarikçi e-postası",
  "suppliers.emailHelp":
    "İsteğe bağlı — henüz e-posta gönderilmez; bağlantıyı kopyalayıp kendiniz iletirsiniz.",
  "suppliers.fields": "İstenen alanlar",
  "suppliers.fieldsHelp": "Tedarikçinin doldurmasını istediğiniz pasaport alanlarını seçin.",
  "suppliers.expiresInDays": "Geçerlilik süresi (gün)",
  "suppliers.expiresHelp": "1–90 gün. Bu süre sonunda bağlantı çalışmaz.",
  "suppliers.note": "Dahili not",
  "suppliers.created": "Davet oluşturuldu",
  "suppliers.linkTitle": "Paylaşım bağlantısı",
  "suppliers.linkOnce": "Bu bağlantı yalnızca bir kez gösterilir — şimdi kopyalayıp tedarikçiye iletin.",
  "suppliers.copyLink": "Bağlantıyı kopyala",
  "suppliers.linkCopied": "Bağlantı kopyalandı",
  "suppliers.linkUnavailable": "Bağlantılar yalnızca davet oluşturulurken bir kez gösterilir.",
  "suppliers.empty": "Henüz tedarikçi daveti yok",
  "suppliers.supplier": "Tedarikçi",
  "suppliers.expiry": "Son geçerlilik",
  "suppliers.fieldCount": "Alan",
  "suppliers.review": "İncele",
  "suppliers.revoke": "İptal et",
  "suppliers.revokeConfirm": "Bu davet iptal edilsin mi?",
  "suppliers.revokeDescription": "Tedarikçinin bağlantısı anında devre dışı kalır.",
  "suppliers.revoked": "Davet iptal edildi",
  "suppliers.reviewTitle": "Tedarikçi gönderimi",
  "suppliers.submittedBy": "Gönderen",
  "suppliers.submittedAt": "Gönderim",
  "suppliers.appliedAt": "Uygulanma",
  "suppliers.field": "Alan",
  "suppliers.submittedValue": "Gönderilen değer",
  "suppliers.currentValue": "Mevcut değer",
  "suppliers.changed": "değişti",
  "suppliers.same": "aynı",
  "suppliers.apply": "Seçilenleri uygula",
  "suppliers.applied": "Gönderim ürüne uygulandı",
  "suppliers.selectAtLeastOne": "Uygulamak için en az bir alan seçin",
  "suppliers.selectFieldRequired": "İstemek için en az bir alan seçin",

  "supplierStatus.pending": "bekliyor",
  "supplierStatus.submitted": "gönderildi",
  "supplierStatus.applied": "uygulandı",
  "supplierStatus.revoked": "iptal edildi",
  "supplierStatus.expired": "süresi doldu",

  "access.title": "Kısıtlı erişim",
  "access.grant": "Erişim ver",
  "access.grantTitle": "Yeni erişim izni",
  "access.intro":
    "Tamirci, geri dönüşümcü, gümrük veya iş ortağına bu pasaportun kısıtlı alanlarını da gösteren bir bağlantı verir.",
  "access.role": "Rol",
  "access.holder": "Verilen taraf",
  "access.holderHelp": "Erişimin verildiği kurum veya kişi (dahili kayıt için).",
  "access.holderRequired": "Erişimin kime verildiğini girin",
  "access.expiresInDays": "Geçerlilik süresi (gün)",
  "access.expiresHelp": "1–365 gün. Bu süre sonunda bağlantı çalışmaz.",
  "access.created": "Erişim izni oluşturuldu",
  "access.linkTitle": "Erişim bağlantısı",
  "access.linkOnce": "Bu bağlantı yalnızca bir kez gösterilir — şimdi kopyalayıp ilgili tarafa iletin.",
  "access.copyLink": "Bağlantıyı kopyala",
  "access.linkCopied": "Bağlantı kopyalandı",
  "access.empty": "Henüz erişim izni yok",
  "access.expiry": "Bitiş",
  "access.lastAccessed": "Son erişim",
  "access.revoke": "İptal et",
  "access.revokeConfirm": "Bu erişim izni iptal edilsin mi?",
  "access.revokeDescription": "İlgili tarafın bağlantısı derhal çalışmaz olur.",
  "access.revoked": "Erişim izni iptal edildi",

  "accessRole.repairer": "Tamirci",
  "accessRole.recycler": "Geri dönüşümcü",
  "accessRole.customs": "Gümrük",
  "accessRole.partner": "İş ortağı",
  "accessRole.authority": "Yetkili kuruluş",
  "accessRole.refurbisher": "Yenileyici / yeniden imalatçı",
  "accessRole.distributor": "Dağıtıcı",

  "operators.title": "İktisadi işletmeciler",
  "operators.hint":
    "Pasaportlarınızın adını andığı taraflar — imalatçı, ithalatçı, yetkili temsilci, sorumlu işletmeci, DPP hizmet sağlayıcısı. Rol başına bir varsayılan; ürün bazında geçersiz kılınabilir. İmalatçı ve ithalatçı iletişim bilgileri kamuya açık pasaport sayfasında görünür.",
  "operators.new": "Yeni işletmeci",
  "operators.edit": "İşletmeciyi düzenle",
  "operators.saved": "İşletmeci kaydedildi",
  "operators.deleted": "İşletmeci kaldırıldı",
  "operators.deleteConfirm":
    "Bu işletmeci kaldırılsın mı? Yayımlanmış pasaportlar yayım anındaki kopyayı korur.",
  "operators.empty": "Henüz işletmeci yok — en azından imalatçıyı (kendi firmanızı) ekleyin.",
  "operators.role": "Rol",
  "operators.default": "varsayılan",
  "operators.self": "bu organizasyon",
  "operators.setDefault": "Bu rol için organizasyon varsayılanı",
  "operators.legalName": "Tescilli unvan",
  "operators.tradeName": "Ticari ad",
  "operators.streetAddress": "Adres",
  "operators.postalCode": "Posta kodu",
  "operators.region": "İl / bölge",
  "operators.email": "E-posta",
  "operators.phone": "Telefon",
  "operators.website": "Web sitesi",
  "operators.identifier": "Tanımlayıcı",
  "operators.identifierScheme": "Tanımlayıcı şeması",
  "operators.identifierValue": "Tanımlayıcı",
  "operators.identifierHelp": "GLN 13 hane, MERSİS 16, VKN 10, LEI 20, EORI/VAT ülke kodu + haneler.",
  "operators.noIdentifier": "Yok",
  "operators.eori": "EORI",
  "operators.eoriHelp": "İthalatçının AB gümrük numarası (ülke kodu + en fazla 15 karakter).",
  "operators.contactTier": "İletişim görünürlüğü",
  "operators.contactTierHelp": "Kamu: pasaport sayfasında gösterilir. Kısıtlı: yalnızca erişim izniyle.",
  "operators.tier.public": "kamu",
  "operators.tier.restricted": "kısıtlı",
  "operators.mandateFrom": "Yetki başlangıcı",
  "operators.mandateUntil": "Yetki bitişi",
  "operators.productTitle": "Bu ürünün iktisadi işletmecileri",
  "operators.productHint":
    "Bu ürün için organizasyon varsayılanlarını geçersiz kılın. Yayımlama, rol başına geçerli tarafı pasaporta dondurur.",
  "operators.useDefault": "Org varsayılanı",
  "operators.noneNamed": "Kimse belirtilmedi",
  "operators.assigned": "İşletmeciler atandı",
  "operators.source.product": "ürün bazlı",
  "operators.source.default": "org varsayılanı",
  "operators.source.none": "yok",

  "operatorRole.manufacturer": "İmalatçı",
  "operatorRole.importer": "İthalatçı",
  "operatorRole.authorised_representative": "Yetkili temsilci",
  "operatorRole.responsible_person": "Sorumlu işletmeci",
  "operatorRole.dpp_service_provider": "DPP hizmet sağlayıcısı",
  "operatorRole.distributor": "Dağıtıcı",

  "operatorScheme.GLN": "GS1 GLN",
  "operatorScheme.EORI": "EORI",
  "operatorScheme.VAT": "Vergi (VAT) numarası",
  "operatorScheme.LEI": "LEI",
  "operatorScheme.NTR": "Ticaret sicil no",
  "operatorScheme.MERSIS": "MERSİS",
  "operatorScheme.VKN": "Vergi kimlik no (VKN)",
  "operatorScheme.DUNS": "D-U-N-S",
  "operatorScheme.LOCAL": "Diğer / yerel",

  "accessStatus.active": "etkin",
  "accessStatus.revoked": "iptal edildi",
  "accessStatus.expired": "süresi doldu",

  "passports.title": "Pasaportlar",
  "passports.new": "Pasaport oluştur",
  "passports.exportCustomsCsv": "Gümrük listesi (CSV)",
  "passports.createTitle": "Yeni pasaport",
  "passports.created": "Pasaport oluşturuldu",
  "passports.product": "Ürün",
  "passports.version": "Sürüm",
  "passports.filterAll": "Tüm ürünler",
  "passports.empty": "Henüz pasaport yok",
  "passports.selectProduct": "Ürün seçin…",
  "passports.qr": "QR",
  "passports.qrShow": "QR'ı göster",
  "passports.qrHint": "Halka açık pasaport sayfasını açmak için tarayın",

  "passportStatus.draft": "taslak",
  "passportStatus.published": "yayımlandı",
  "passportStatus.archived": "arşivli",

  "passport.notFound": "Pasaport bulunamadı",
  "passport.backToList": "Tüm pasaportlar",
  "passport.publish": "Yayımla",
  "passport.republish": "Yeniden yayımla",
  "passport.published": "Pasaport yayımlandı",
  "passport.publishBlocked": "Yayımlama engellendi — eksik veya geçersiz alanlar:",
  "passport.archiveConfirm": "Bu pasaport arşivlensin mi?",
  "passport.archiveDescription": "Arşivlenen pasaport artık yayımlanamaz.",
  "passport.archived": "Pasaport arşivlendi",
  "passport.versions": "Sürümler",
  "passport.noVersions": "Henüz yayımlanmadı",
  "passport.verify": "Zinciri doğrula",
  "passport.chainValid": "Sürüm zinciri sağlam",
  "passport.chainBroken": "Zincir şu sürümde kırık:",
  "passport.publicPage": "Kamu sayfası",
  "passport.qr": "QR indir",
  "passport.currentVersion": "Güncel sürüm",
  "passport.productCompliance": "Güncel ürün uyumu",
  "passport.publishedCompliance": "Yayımlanan uyum",
  "passport.drift":
    "Ürün verisi son yayımdan sonra değişti — kamu pasaportunu güncellemek için yeniden yayımlayın.",
  "passport.hash": "Özet (hash)",
  "passport.registry": "AB DPP Registry",
  "passport.register": "Registry'ye kaydet",
  "passport.registered": "Pasaport AB DPP Registry'ye kaydedildi",
  "passport.registerFailed": "Kayıt başarısız",
  "passport.notRegistered": "Kayıtlı değil",
  "passport.registeredAt": "Kayıt tarihi",
  "passport.registryUid": "Kayıtlı UID",
  "passport.registryAttempts": "deneme",
  "passport.registerHint": "Yalnızca yayımlanmış bir pasaport kaydedilebilir.",
  "passport.euMissing":
    "AB DPP Registry bu pasaportu reddedecek: aşağıdaki alanları doldurup yeni sürüm yayımlayın.",
  "passport.euMissingRegistrant": "Kaydeden kimliği (AB registry kaydını tamamlayın)",
  "passport.euMissingManufacturer": "Üretici posta adresi (herkese açık bir üretici operatörü ekleyin)",
  "passport.customs": "Gümrük ve kayıt",
  "passport.customsHint":
    "Kayıt tanımlayıcısını (URI) ve emtia kodlarını ihracat beyannamesine yazın — gümrük bunları sicille eşleştirir.",
  "passport.customsUpi": "UPI (tek ve benzersiz ürün tanımlayıcısı)",
  "passport.customsUri": "Kayıt tanımlayıcısı (URI)",
  "passport.customsUriPending": "Doğrulama sonrası sicil tarafından atanır",
  "passport.customsCn": "CN emtia kodu",
  "passport.customsGtip": "GTİP",
  "passport.customsNone": "Henüz kayıtlı değil — pasaport sicile kaydedildiğinde UPI ve URI burada görünür.",
  "passport.retention": "Saklama ve yedek",
  "passport.retentionHint":
    "Pasaport, belgeleri ve talimatları SON birim piyasaya arz edildikten sonra {years} yıl çevrimiçi kalmalıdır. İlk yayın arz tarihini belirler; burada düzeltin ya da son birim tarihini ekleyin.",
  "passport.placedOnMarket": "Piyasaya arz tarihi",
  "passport.lastUnitPlacedOnMarket": "Son birimin arz tarihi",
  "passport.retainUntil": "Çevrimiçi tutulacak tarih",
  "passport.datesSaved": "Arz tarihleri kaydedildi",
  "passport.backup": "Yedek kopya",
  "passport.backupNotConfigured": "yedek barındırıcı yapılandırılmamış",
  "passport.backupNone": "henüz kopya yok",
  "passport.backupStoredAt": "Kopya alındı",
  "backupStatus.pending": "bekliyor",
  "backupStatus.stored": "yedek barındırıcıda",
  "backupStatus.failed": "başarısız — yeniden denenecek",
  "org.status": "Durum",
  "orgStatus.active": "aktif",
  "orgStatus.suspended": "askıda",
  "orgStatus.wound_down": "tasfiye — salt okunur, pasaportlar yayında",
  "audience.label": "Kimler görebilir",
  "audience.help":
    "Yalnızca kısıtlı öğeler için. Hiçbiri seçili değilse her erişim rolü görür; yetkili kuruluş her zaman her şeyi görür.",
  "audience.everyone": "tüm kısıtlı roller",
  "passport.lineage": "Soy bağı",
  "passport.lineageHint":
    "Yenilenmiş, yeniden imal edilmiş veya yeniden amaçlandırılmış bir ürünün pasaportu orijinal pasaporta bağlanır (aynı organizasyonda kimlikle, başka işletmecide URL ile). Bir sonraki yayında pasaporta yazılır.",
  "passport.originalPassport": "Orijinal pasaport",
  "passport.lineageReason": "Neden",
  "passport.lineageSaved": "Soy bağı kaydedildi",
  "lineageReason.refurbished": "yenilenmiş",
  "lineageReason.remanufactured": "yeniden imal edilmiş",
  "lineageReason.repurposed": "yeniden amaçlandırılmış",
  "lineageReason.reissued": "yeniden düzenlenmiş",
  "passport.exportOfficial": "Resmi döküm",
  "passport.exportHint": "Yetkili kuruluş için tam katmanlı kopya (Md. 27(15)) — denetim kaydına girer.",
  "passport.viewJson": "JSON görüntüle",
  "passport.downloadJsonLd": "JSON-LD indir",
  "jsonViewer.search": "JSON içinde ara",
  "jsonViewer.mode": "Görünüm modu",
  "jsonViewer.tree": "Ağaç",
  "jsonViewer.raw": "Ham",
  "jsonViewer.expandAll": "Tümünü genişlet",
  "jsonViewer.collapseAll": "Tümünü daralt",
  "jsonViewer.expand": "Genişlet",
  "jsonViewer.collapse": "Daralt",
  "jsonViewer.copy": "Kopyala",
  "jsonViewer.copied": "Kopyalandı",
  "jsonViewer.matches": "{count} eşleşme",
  "jsonViewer.noMatches": "Eşleşme yok",
  "org.enterpriseSize": "İşletme büyüklüğü",
  "org.enterpriseSizeSaved": "İşletme büyüklüğü kaydedildi",
  "enterpriseSize.micro": "mikro (< 10 çalışan)",
  "enterpriseSize.small": "küçük (< 50 çalışan)",
  "enterpriseSize.medium": "orta (< 250 çalışan)",
  "enterpriseSize.large": "büyük (250+ çalışan)",
  "disclosure.title": "Satılmayan tüketici ürünleri — imha beyanı",
  "disclosure.hint":
    "ESPR / TR taslak yönetmelik Md. 24: her yıl atılan satılmayan tüketici ürünlerinin sayısını ve ağırlığını, nedenlerini, dayanılan muafiyeti, geri kazanım paylarını ve alınan tedbirleri açıklayın. Yayımlama yılı dondurur ve aşağıdaki kamu sayfasında listeler.",
  "disclosure.sizeUnknown":
    "Organizasyon ayarlarında işletme büyüklüğünüzü belirtin — imha yasağının size uygulanıp uygulanmadığına o karar verir.",
  "disclosure.banApplies":
    "Giyim, aksesuar ve ayakkabıda imha yasağı organizasyonunuza {date} tarihinden beri uygulanıyor.",
  "disclosure.banFuture":
    "İmha yasağı organizasyonunuza {date} tarihinden itibaren uygulanacak. O zamana kadar beyan gönüllüdür.",
  "disclosure.exempt": "Mikro ve küçük işletmeler imha yasağından muaftır — beyan gönüllüdür.",
  "disclosure.publicPage": "Kamu sayfası",
  "disclosure.noReports": "Henüz beyan yok — aşağıdan bir mali yıl seçip satır ekleyin.",
  "disclosure.year": "Mali yıl",
  "disclosure.lines": "Satır",
  "disclosure.publishedAt": "Yayım",
  "disclosure.status.draft": "taslak",
  "disclosure.status.published": "yayımlandı",
  "disclosure.saveDraft": "Taslağı kaydet",
  "disclosure.saved": "Taslak kaydedildi",
  "disclosure.publish": "Yayımla",
  "disclosure.published": "Beyan yayımlandı",
  "disclosure.publishConfirm": "Bu yılın beyanı yayımlansın mı?",
  "disclosure.publishDescription": "Yayımlanan yıl dondurulur ve kamu sayfasında görünür. Geri alınamaz.",
  "disclosure.locked": "Bu yıl yayımlandı ve donduruldu.",
  "disclosure.note": "Not (isteğe bağlı)",
  "disclosure.category": "Ürün türü",
  "disclosure.categoryNote": "Tür açıklaması",
  "disclosure.units": "Atılan adet",
  "disclosure.weightKg": "Ağırlık (kg)",
  "disclosure.reason": "Neden",
  "disclosure.reasonNote": "Neden açıklaması",
  "disclosure.exemptionRef": "Dayanılan muafiyet",
  "disclosure.reusePct": "Yeniden kullanıma hazırlama",
  "disclosure.recycledPct": "Geri dönüşüm",
  "disclosure.otherRecoveryPct": "Diğer geri kazanım",
  "disclosure.disposedPct": "Bertaraf",
  "disclosure.sharesInvalid": "paylar toplamı %100 olmalı",
  "disclosure.measures": "İmhayı önlemek için alınan/planlanan tedbirler",
  "disclosure.addLine": "Satır ekle",
  "disposalCategory.apparel": "Giyim",
  "disposalCategory.clothing_accessories": "Giyim aksesuarı",
  "disposalCategory.footwear": "Ayakkabı",
  "disposalCategory.other": "Diğer",
  "disposalReason.health_safety": "Sağlık, hijyen, güvenlik",
  "disposalReason.damaged_unrepairable": "Onarılamaz hasar",
  "disposalReason.unfit_for_purpose": "Amaca uygun değil",
  "disposalReason.donation_refused": "Bağış reddedildi",
  "disposalReason.not_reusable": "Yeniden kullanıma uygun değil",
  "disposalReason.ip_infringement": "Fikri mülkiyet ihlali (taklit)",
  "disposalReason.least_impact_option": "Çevreye en az etkili seçenek",
  "disposalReason.other": "Diğer",
  "passport.readiness": "Yayın durumu",
  "passport.checkComplete": "Zorunlu alanların tamamı dolu",
  "passport.checkMissing": "{count} zorunlu alan hâlâ eksik",
  "passport.checkPublished": "v{version} olarak yayımlandı",
  "passport.checkUnpublished": "Yayımlanmadı — kamu sayfası henüz yayında değil",
  "passport.checkFresh": "Kamu sayfası güncel ürün verisiyle aynı",
  "passport.checkArchived": "Arşivlendi — bu pasaport artık yayımlanamaz",
  "passport.missingFields": "Eksik zorunlu alanlar",
  "passport.openProduct": "Ürünü aç",
  "passport.publicAccess": "Kamu sayfası ve QR",
  "passport.publicUrl": "Kamu adresi",
  "passport.copyLink": "Bağlantıyı kopyala",
  "passport.linkCopied": "Bağlantı kopyalandı",
  "passport.notPublicYet": "Kamu sayfası ve QR kodu, pasaportu yayımladığınızda yayına girer.",
  "passport.identity": "Kimlik",
  "passport.identifier": "Tanımlayıcı",
  "passport.versionsHint": "Her yayım, zincirlenmiş ve değişiklik belli eden bir sürüm ekler.",
  "passport.latest": "güncel",
  "passport.moreFields": "+{count} tane daha",

  "registryStatus.pending": "Beklemede",
  "registryStatus.processing": "Registry doğrulaması bekleniyor",
  "registryStatus.registered": "Kayıtlı",
  "registryStatus.failed": "Başarısız",

  "orgSettings.title": "Organizasyon ayarları",
  "orgSettings.members": "Üyeler",
  "orgSettings.addMember": "Üye ekle",
  "orgSettings.memberAdded": "Üye eklendi",
  "orgSettings.memberRemoved": "Üye kaldırıldı",
  "orgSettings.roleChanged": "Rol güncellendi",
  "orgSettings.removeConfirm": "Bu üye kaldırılsın mı?",
  "orgSettings.removeDescription": "Kullanıcı bu organizasyona erişimini kaybeder.",
  "orgSettings.user": "Kullanıcı",
  "orgSettings.email": "E-posta",
  "orgSettings.role": "Rol",
  "orgSettings.noMembers": "Üye yok",
  "orgSettings.yourRole": "Rolünüz",
  "orgSettings.usage": "Bu yıl yayımlanan pasaport",

  "apiKeys.title": "API anahtarları",
  "apiKeys.create": "Anahtar oluştur",
  "apiKeys.createTitle": "Yeni API anahtarı",
  "apiKeys.intro":
    "Bir ERP bağlayıcısına (Logo, Nebim, SAP…) bu organizasyonun verilerine makine erişimi verir. Anahtarın kapsamları tek yetkisidir.",
  "apiKeys.name": "Ad",
  "apiKeys.nameHelp": 'Entegrasyon için bir etiket, örn. "Logo bağlayıcısı".',
  "apiKeys.nameRequired": "Anahtar için bir ad girin",
  "apiKeys.scopes": "Kapsamlar",
  "apiKeys.scopeRequired": "En az bir kapsam seçin",
  "apiKeys.expiresInDays": "Geçerlilik süresi (gün)",
  "apiKeys.expiresHelp": "İsteğe bağlı — süresiz anahtar için boş bırakın.",
  "apiKeys.created": "API anahtarı oluşturuldu",
  "apiKeys.tokenTitle": "API anahtarı",
  "apiKeys.tokenOnce": "Bu anahtar yalnızca bir kez gösterilir — şimdi kopyalayıp bağlayıcınızda saklayın.",
  "apiKeys.copy": "Anahtarı kopyala",
  "apiKeys.copied": "Kopyalandı",
  "apiKeys.empty": "Henüz API anahtarı yok",
  "apiKeys.keyPrefix": "Anahtar",
  "apiKeys.lastUsed": "Son kullanım",
  "apiKeys.expiry": "Bitiş",
  "apiKeys.noExpiry": "süresiz",
  "apiKeys.revoke": "İptal et",
  "apiKeys.revokeConfirm": "Bu API anahtarı iptal edilsin mi?",
  "apiKeys.revokeDescription": "Bu anahtarı kullanan bağlayıcı derhal çalışmaz olur.",
  "apiKeys.revoked": "API anahtarı iptal edildi",

  "apiKeyStatus.active": "etkin",
  "apiKeyStatus.revoked": "iptal edildi",
  "apiKeyStatus.expired": "süresi doldu",

  "webhooks.title": "Webhook'lar",
  "webhooks.create": "Webhook ekle",
  "webhooks.createTitle": "Yeni webhook",
  "webhooks.intro":
    "Pasaport veya ürün değişikliklerinde uç noktanıza imzalı (istek gövdesi üzerinde HMAC-SHA256) olay bildirimleri gönderir.",
  "webhooks.url": "Uç nokta URL'si",
  "webhooks.urlHelp": "İmzalı POST isteklerini alan HTTPS uç noktası.",
  "webhooks.urlRequired": "Uç nokta URL'sini girin",
  "webhooks.events": "Olaylar",
  "webhooks.eventRequired": "En az bir olay seçin",
  "webhooks.created": "Webhook oluşturuldu",
  "webhooks.secretTitle": "İmzalama anahtarı",
  "webhooks.secretOnce":
    "Bu imzalama anahtarı yalnızca bir kez gösterilir — şimdi kopyalayın ve X-DPP-Signature başlığını doğrulamak için kullanın.",
  "webhooks.copy": "Anahtarı kopyala",
  "webhooks.copied": "Kopyalandı",
  "webhooks.empty": "Henüz webhook yok",
  "webhooks.lastDelivery": "Son teslimat",
  "webhooks.test": "Test gönder",
  "webhooks.testOk": "Test bildirimi iletildi",
  "webhooks.testFailed": "Test bildirimi başarısız",
  "webhooks.enable": "Etkinleştir",
  "webhooks.enabled": "Webhook etkinleştirildi",
  "webhooks.disable": "Devre dışı bırak",
  "webhooks.disabled": "Webhook devre dışı bırakıldı",
  "webhooks.disableConfirm": "Bu webhook devre dışı bırakılsın mı?",
  "webhooks.disableDescription": "Yeniden etkinleştirilene kadar olay iletilmez.",
  "webhooks.deliveries": "Teslimatlar",
  "webhooks.deliveriesTitle": "Webhook teslimatları",
  "webhooks.noDeliveries": "Henüz teslimat yok",
  "webhooks.event": "Olay",
  "webhooks.attempts": "Deneme",
  "webhooks.lastError": "Son hata",
  "webhooks.retry": "Yeniden dene",
  "webhooks.retryOk": "Teslimat başarılı",
  "webhooks.retryFailed": "Teslimat yine başarısız",

  "webhookStatus.active": "etkin",
  "webhookStatus.disabled": "devre dışı",

  "deliveryStatus.pending": "bekliyor",
  "deliveryStatus.delivered": "iletildi",
  "deliveryStatus.failed": "başarısız",

  "role.owner": "sahip",
  "role.member": "üye",
  "role.admin": "yönetici",
  "role.editor": "editör",
  "role.viewer": "görüntüleyici",

  "login.title": "Oturum aç",
  "login.continue": "Devam et",
  "login.password": "Parola",
  "login.newPassword": "Yeni parola",
  "login.updatePassword": "Parolayı güncelle",
  "login.passwordExpired": "Parolanızın süresi doldu. Devam etmek için yeni bir parola belirleyin.",
  "login.passwordUpdated": "Parola güncellendi. Lütfen yeni parolanızla oturum açın.",
  "login.registerPending":
    "Devam etmek için e-postanızı kontrol edin, ardından aşağıdan giriş yapın. Bu adrese ait bir hesap zaten varsa bunu bildiren bir e-posta gönderdik.",
  "login.mfaPrompt": "Doğrulayıcı uygulamanızdaki 6 haneli kodu girin.",
  "login.mfaCode": "Doğrulama kodu",
  "login.verify": "Doğrula",
  "login.requestFailed": "İstek başarısız oldu",
  "login.subtitle": "Organizasyon panelinize giriş yapın.",
  "login.explore": "Yeni misiniz? Ürünü keşfedin →",
  "login.register": "Hesap oluştur",
  "login.noAccount": "Hesabınız yok mu? Kaydolun →",
  "login.haveAccount": "Zaten hesabınız var mı? Giriş yapın →",
  "login.registerTitle": "Hesabınızı oluşturun",
  "login.registerSubtitle": "Dakikalar içinde dijital ürün pasaportları yayınlamaya başlayın.",
  "login.mfaTitle": "İki adımlı doğrulama",
  "login.expiredTitle": "Yeni parola belirleyin",
  "login.back": "← Girişe dön",
  "login.forgotPassword": "Parolanızı mı unuttunuz?",
  "login.name": "Ad Soyad",
  "login.nameHelp": "İsteğe bağlı — ekip arkadaşlarınıza görünür.",
  "login.emailPlaceholder": "siz@sirketiniz.com",
  "login.pwdWeak": "Parola henüz aşağıdaki gereksinimleri karşılamıyor.",
  "login.pwdRule.length": "En az 12 karakter",
  "login.pwdRule.lower": "Bir küçük harf",
  "login.pwdRule.upper": "Bir büyük harf",
  "login.pwdRule.digit": "Bir rakam",
  "login.pwdRule.symbol": "Bir sembol",
  "login.inviteNotice": "Davetinize devam etmek için giriş yapın veya hesap oluşturun.",
  "invites.title": "Davetler",
  "invites.create": "Davet et",
  "invites.createTitle": "Ekip arkadaşı davet et",
  "invites.intro": "Bu organizasyona seçtiğiniz rolle katılması için e-posta daveti gönderin.",
  "invites.email": "E-posta",
  "invites.emailRequired": "E-posta adresi gerekli",
  "invites.send": "Davet gönder",
  "invites.created": "Davet oluşturuldu",
  "invites.linkTitle": "Davet bağlantısı",
  "invites.linkReady": "Davet gönderildi. Bu tek kullanımlık bağlantıyı da paylaşabilirsiniz:",
  "invites.copy": "Bağlantıyı kopyala",
  "invites.copied": "Bağlantı kopyalandı",
  "invites.expiry": "Geçerlilik",
  "invites.empty": "Henüz davet yok",
  "invites.revoke": "İptal et",
  "invites.revokeConfirm": "Bu davet iptal edilsin mi?",
  "invites.revoked": "Davet iptal edildi",
  "invites.acceptTitle": "Davet edildiniz",
  "invites.acceptBody": "Şu organizasyona katılmaya davet edildiniz:",
  "invites.acceptButton": "Daveti kabul et",
  "invites.acceptSuccess": "Organizasyona katıldınız",
  "invites.acceptSignIn": "Giriş yapın veya hesap oluşturun",
  "invites.acceptSignInPrompt": "Kabul etmek için giriş yapın veya kaydolun — şu e-postayı kullanın:",
  "invites.acceptWrongAccount": "Farklı bir hesapla giriş yaptınız. Şununla giriş yapın:",
  "invites.acceptMissingToken": "Bu davet bağlantısında token eksik.",
  "invites.acceptErrorTitle": "Davet kullanılamıyor",
  "invites.acceptSwitch": "Oturumu kapatıp hesap değiştir",
  "invites.loading": "Davetiniz yükleniyor…",
  "inviteStatus.pending": "Bekliyor",
  "inviteStatus.accepted": "Kabul edildi",
  "inviteStatus.revoked": "İptal edildi",
  "inviteStatus.expired": "Süresi doldu",
  "verify.missingToken": "Bu doğrulama bağlantısında token eksik.",
  "verify.successTitle": "E-posta doğrulandı",
  "verify.successBody": "Teşekkürler — e-posta adresiniz onaylandı.",
  "verify.errorTitle": "Doğrulama başarısız",
  "verify.goDashboard": "Panele git",
  "verify.goLogin": "Girişe git",
  "verify.resend": "Yeniden gönder",
  "verify.resent": "Doğrulama e-postası gönderildi",
  "verify.bannerText": "Lütfen e-posta adresinizi doğrulayın.",
  "verify.verifying": "E-posta adresiniz doğrulanıyor…",
  "verify.enterCode": "Kodu gir",
  "verify.modalTitle": "E-postanı doğrula",
  "verify.modalBody": "6 haneli doğrulama kodunu şu adrese gönderdik:",
  "verify.codeLabel": "Doğrulama kodu",
  "verify.codeInvalid": "E-postadaki 6 haneli kodu girin.",
  "verify.submit": "Doğrula",
  "verify.verified": "E-posta adresiniz doğrulandı.",
  "forgot.title": "Parolanızı sıfırlayın",
  "forgot.subtitle":
    "Hesap e-posta adresinizi girin; yeni bir parola belirlemeniz için size bir bağlantı gönderelim.",
  "forgot.submit": "Sıfırlama bağlantısı gönder",
  "forgot.sentTitle": "E-postanızı kontrol edin",
  "forgot.sentBody":
    "Bu e-postaya ait bir hesap varsa, parola sıfırlama bağlantısı yolda. Bağlantının süresi kısadır, lütfen kısa süre içinde kullanın.",
  "reset.title": "Yeni parola belirleyin",
  "reset.subtitle": "Hesabınız için güçlü bir yeni parola seçin.",
  "reset.submit": "Parolayı güncelle",
  "reset.successTitle": "Parola güncellendi",
  "reset.successBody": "Parolanız sıfırlandı. Yeni parolanızla oturum açın.",
  "reset.errorTitle": "Sıfırlama bağlantısı sorunu",
  "reset.missingToken": "Bu sıfırlama bağlantısında token eksik. Devam etmek için yeni bir tane isteyin.",
  "reset.requestNew": "Yeni bağlantı iste",
  "nav.billing": "Faturalama",
  "billing.usageTitle": "Paket ve kullanım",
  "billing.plan": "Paket",
  "billing.period": "Dönem",
  "billing.kind": "Tür",
  "billing.annualFee": "Yıllık ücret",
  "billing.viewsThisYear": "Bu yıl görüntülenme",
  "billing.viewsThisMonth": "Bu ay görüntülenme",
  "billing.products": "Ürünler",
  "billing.unlimited": "Limitsiz",
  "billing.adminTitle": "Operatör işlemleri",
  "billing.adminHint": "Önceki ayı kapatarak taslak faturaları üretin, sonra kesip tahsil edin.",
  "billing.closeMonth": "Önceki ayı kapat",
  "billing.monthClosed": "Ay kapatıldı — taslak faturalar üretildi",
  "billing.invoicesTitle": "Faturalar",
  "billing.noInvoices": "Henüz fatura yok",
  "billing.views": "Görüntülenme",
  "billing.amount": "Tutar",
  "billing.due": "Vade",
  "billing.issue": "Kes",
  "billing.issued": "Fatura kesildi",
  "billing.markPaid": "Ödendi işaretle",
  "billing.markedPaid": "Fatura ödendi olarak işaretlendi",
  "billing.void": "İptal",
  "billing.voided": "Fatura iptal edildi",
  "billing.voidConfirm": "Bu fatura iptal edilsin mi?",
  "billing.delinquentNotice":
    "Bu organizasyonun vadesi geçmiş faturası var. Ödenene kadar düzenleme kilitli; public pasaport sayfaları erişilebilir kalır.",
  "billing.delinquentBanner": "Bir faturanın vadesi geçti — ödenene kadar düzenleme kilitli.",
  "billing.viewLimitNotice":
    "Ücretsiz paketin yıllık görüntülenme hakkı doldu. Yükseltene kadar düzenleme kilitli; herkese açık pasaport sayfaları yayında kalır.",
  "billing.viewLimitBanner":
    "Ücretsiz paketin görüntülenme hakkı doldu — düzenlemeye devam etmek için paketi yükseltin.",
  "billing.viewInvoices": "Faturaları gör →",
  "billing.disabled": "Bu kurulumda faturalama mevcut değil.",
  "invoiceKind.subscription": "Abonelik",
  "invoiceKind.usage": "Kullanım",
  "invoiceStatus.draft": "Taslak",
  "invoiceStatus.issued": "Kesildi",
  "invoiceStatus.paid": "Ödendi",
  "invoiceStatus.overdue": "Vadesi geçti",
  "invoiceStatus.void": "İptal",
  "login.pitch1": "Standart-yerlisi pasaportlar: GS1 Digital Link, JSON-LD, EN 1821x",
  "login.pitch2": "İmzalı, sürümlü, değişmez yayınlar ve Doğrulanmış rozeti",
  "login.pitch3": "Tedarik zinciri, bileşenler, LCA ve döngüsellik tek public sayfada",
  "login.pitch4": "Tüketici, tamirci, geri dönüşümcü ve gümrük için katmanlı erişim",

  "dashboard.account": "Hesap",
  "dashboard.name": "Ad",
  "dashboard.mfaEnabled": "Etkin ✅",
  "dashboard.mfaDisabled": "Devre dışı ⚠️",
  "dashboard.downloadData": "Verilerimi indir (GDPR)",
  "dashboard.sessions": "Etkin oturumlar",
  "dashboard.sessionsEmpty": "Etkin oturum yok",
  "dashboard.sessionStarted": "Başlangıç",
  "dashboard.thisDevice": "bu cihaz",
  "dashboard.revoke": "Sonlandır",
  "dashboard.permissions": "İzinler",
  "dashboard.noPermissions": "Koleksiyon düzeyinde izin yok.",
  "dashboard.mfaTitle": "İki adımlı doğrulamayı etkinleştir",
  "dashboard.mfaStepStart": "Başla",
  "dashboard.mfaStepVerify": "Tara ve doğrula",
  "dashboard.mfaStepEnabled": "Etkin",
  "dashboard.mfaStart": "MFA kurulumunu başlat",
  "dashboard.mfaSecretHint":
    "Bu gizli anahtarı doğrulayıcı uygulamanıza ekleyin, ardından güncel kodu girin.",
  "dashboard.mfaConfirm": "Onayla",
  "dashboard.mfaDone": "MFA etkinleştirildi. Bir sonraki oturum açışınızda kod istenecek.",
  "dashboard.mfaEnrollFailed": "Kayıt başlatılamadı",
  "dashboard.mfaInvalidCode": "Geçersiz kod",

  "profile.tabProfile": "Hesap",
  "profile.tabSecurity": "Güvenlik",
  "profile.tab2fa": "İki adımlı doğrulama",
  "profile.tabDevices": "Cihazlar",
  "profile.tabOrgs": "Organizasyonlar",
  "profile.tabActivity": "Etkinlik",
  "profile.accountInfo": "Hesap bilgileri",
  "profile.identity": "Kimlik",
  "profile.displayName": "Görünen ad",
  "profile.displayNameHint": "Uygulamada görünen adınız.",
  "profile.saved": "Profil güncellendi.",
  "profile.security": "Şifre",
  "profile.password": "Şifre",
  "profile.currentPassword": "Mevcut şifre",
  "profile.newPassword": "Yeni şifre",
  "profile.confirmNewPassword": "Yeni şifre (tekrar)",
  "profile.changePassword": "Şifreyi değiştir",
  "profile.passwordMismatch": "Yeni şifreler eşleşmiyor.",
  "profile.passwordChanged": "Şifre değiştirildi — lütfen tekrar giriş yapın.",
  "profile.passwordChangeWarning": "Şifrenizi değiştirmek tüm cihazlardaki oturumunuzu kapatır.",
  "profile.mfaActive": "İki adımlı doğrulama açık",
  "profile.mfaActiveHint": "Hesabınız bir doğrulayıcı uygulamayla korunuyor.",
  "profile.mfaOffHint": "Hesabınızı daha iyi korumak için ikinci bir doğrulama ekleyin.",
  "profile.mfaDisable": "İki adımlı doğrulamayı kapat",
  "profile.mfaDisableHint": "Kapatmak için şifrenizi ve güncel bir kodu girin.",

  "profile.memberSince": "Üyelik: {date}",
  "profile.emailVerified": "Doğrulandı",
  "profile.emailUnverified": "Doğrulanmadı",
  "profile.verifyNow": "Doğrula",
  "profile.demoAccount": "Demo hesap",
  "profile.securityScore": "Hesap güvenliği",
  "profile.scoreStrong": "Güçlü",
  "profile.scoreFair": "Orta",
  "profile.scoreWeak": "Zayıf",
  "profile.userId": "Kullanıcı kimliği",
  "profile.permissions": "Yetkiler",
  "profile.permissionsCount": "{count} yetki",
  "profile.preferences": "Tercihler",
  "profile.preferencesHint": "Dil ve tema yalnızca bu cihazda hatırlanır.",
  "profile.theme": "Tema",
  "profile.themeLight": "Açık",
  "profile.themeDark": "Koyu",
  "profile.privacy": "Veri ve gizlilik",
  "profile.privacyHint":
    "Hesabınıza dair saklanan her şeyi — profil, oturumlar ve hesap olayları — JSON olarak indirin.",

  "profile.checklist": "Güvenlik kontrol listesi",
  "profile.checklistDone": "{total} adımdan {done} tamam",
  "profile.checkEmail": "E-posta adresinizi doğrulayın",
  "profile.checkEmailDone": "Adresiniz onaylandı.",
  "profile.checkEmailTodo": "Hesap kurtarmanın size ulaşabilmesi için onaylayın.",
  "profile.checkMfa": "İki adımlı doğrulamayı açın",
  "profile.checkMfaDone": "Girişinizi bir doğrulayıcı uygulama koruyor.",
  "profile.checkMfaTodo": "Tek başına şifre tek bir kırılma noktasıdır.",
  "profile.checkPassword": "Şifrenizi güncel tutun",
  "profile.checkPasswordDone": "Son değişiklik: {date}.",
  "profile.checkPasswordTodo": "{days} gündür değişmedi — yenileme zamanı.",
  "profile.checkAction": "Düzelt",
  "profile.passwordAge": "Son değişiklik",
  "profile.passwordExpires": "Geçerlilik sonu",
  "profile.passwordNoPolicy": "Bu kurulumda şifrelerin süresi dolmaz.",

  "profile.devices": "Giriş yapılan cihazlar",
  "profile.devicesHint": "Bir cihazı sonlandırdığınızda oturumu anında kapanır.",
  "profile.deviceUnknown": "Bilinmeyen cihaz",
  "profile.sessionExpires": "Bitiş",
  "profile.mfaVerified": "MFA doğrulandı",
  "profile.revoked": "Oturum sonlandırıldı.",
  "profile.revokeConfirm": "Bu oturum sonlandırılsın mı?",
  "profile.revokeDescription": "O cihazın yeniden giriş yapması gerekir.",
  "profile.revokeOthers": "Diğer cihazları çıkar",
  "profile.revokeOthersConfirm": "Diğer tüm cihazlar çıkarılsın mı?",
  "profile.revokeOthersDescription": "Yalnızca bu cihaz açık kalır.",
  "profile.revokedOthers": "{count} oturum sonlandırıldı.",

  "profile.orgs": "Organizasyonlarınız",
  "profile.orgsHint": "Çalışabileceğiniz kiracılar ve her birindeki rolünüz.",
  "profile.orgActive": "Seçili",
  "profile.orgSwitch": "Buna geç",
  "profile.orgsEmpty": "Henüz hiçbir organizasyona üye değilsiniz.",

  "profile.activity": "Son hesap etkinliği",
  "profile.activityHint": "Hesabınıza işlenen olaylar, en yeniden başlayarak.",
  "profile.activityEmpty": "Henüz kayıt yok.",

  "profile.mfaScanHint": "Bu kodu doğrulayıcı uygulamanızla (Google Authenticator, 1Password, …) taratın.",
  "profile.mfaManualHint": "Taratamıyor musunuz? Bu anahtarı elle ekleyin:",
  "profile.mfaCode": "6 haneli kod",

  "activityAction.auth.login.success": "Giriş yapıldı",
  "activityAction.auth.login.failure": "Başarısız giriş denemesi",
  "activityAction.auth.logout": "Çıkış yapıldı",
  "activityAction.auth.password.changed": "Şifre değiştirildi",
  "activityAction.auth.password.reset_requested": "Şifre sıfırlama istendi",
  "activityAction.auth.password.reset": "Şifre sıfırlandı",
  "activityAction.auth.account.locked": "Hesap kilitlendi",
  "activityAction.auth.token.reuse_detected": "Oturum belirteci yeniden kullanımı algılandı",
  "activityAction.mfa.enrolled": "İki adımlı doğrulama açıldı",
  "activityAction.mfa.disabled": "İki adımlı doğrulama kapatıldı",
  "activityAction.mfa.challenge.success": "İki adımlı kod kabul edildi",
  "activityAction.mfa.challenge.failure": "İki adımlı kod reddedildi",
  "activityAction.session.revoked": "Oturum sonlandırıldı",
  "activityAction.session.revoked_all": "Tüm oturumlar sonlandırıldı",
  "activityAction.user.updated": "Profil güncellendi",
  "activityAction.user.email_verified": "E-posta doğrulandı",
  "activityAction.user.verification_requested": "Doğrulama e-postası istendi",
  "activityAction.org.invite_accepted": "Davet kabul edildi",

  "dashboard.tileProducts": "Ürün",
  "dashboard.tilePublished": "Yayımlanan pasaport",
  "dashboard.tilePassportsTotal": "Toplam pasaport",
  "dashboard.tileCarriers": "QR / veri taşıyıcı",
  "dashboard.publicQrPages": "herkese açık QR sayfası",
  "dashboard.carriersNote": "üretildi",
  "dashboard.passportStatus": "Pasaport durumu",
  "dashboard.productStatus": "Ürün durumu",
  "dashboard.donutPassports": "pasaport",
  "dashboard.donutProducts": "ürün",
  "dashboard.issuance": "Aylık üretim",
  "dashboard.issuanceHint": "Son 12 ayda yayımlanan pasaport",
  "dashboard.usage": "Paket kullanımı",
  "dashboard.viewsThisYear": "Bu yıl herkese açık görüntülenme",
  "dashboard.productAllowance": "Ürünler",

  "users.title": "Kullanıcılar",
  "users.searchPlaceholder": "E-posta veya rol ara…",
  "users.empty": "Eşleşen kullanıcı yok",
  "users.active": "aktif",
  "users.disabled": "devre dışı",
  "users.enable": "Etkinleştir",
  "users.disable": "Devre dışı bırak",
  "users.delete": "Sil",
  "users.deleteConfirm": "Kullanıcı silinsin mi?",
  "users.deleteWarning": "Bu hesap kalıcı olarak silinir (GDPR):",
  "users.deleteConfirmAction": "Silmeyi onayla",
  "users.updateFailed": "Kullanıcı güncellenemedi",
  "users.deleteFailed": "Silme başarısız oldu",

  "audit.title": "Denetim izi",
  "audit.allActions": "Tüm işlemler",
  "audit.allActors": "Tüm kişiler",
  "audit.filterByAction": "İşleme göre filtrele",
  "audit.filterByActor": "Kişiye göre filtrele",
  "audit.perPage": "/ sayfa",
  "audit.empty": "Denetim kaydı yok",
  "audit.time": "Zaman",
  "audit.action": "İşlem",
  "audit.outcome": "Sonuç",
  "audit.actor": "Aktör",
  "audit.system": "Sistem",
  "audit.success": "başarılı",
  "audit.failure": "başarısız",
  "audit.today": "Bugün",
  "audit.last7": "Son 7 gün",
  "audit.last30": "Son 30 gün",

  "license.title": "Lisans",
  "license.state": "Durum",
  "license.readOnly": "(salt okunur)",
  "license.customer": "Müşteri",
  "license.edition": "Sürüm",
  "license.expires": "Son geçerlilik",
  "license.days": "gün",
  "license.features": "Özellikler",
  "license.seats": "Koltuk",
  "license.advanced": "Gelişmiş",
  "license.fingerprint": "Parmak izi",
  "license.reasons": "Nedenler",
  "license.installTitle": "Lisans yükle / değiştir",
  "license.installHint": "Sağlayıcınızın verdiği imzalı lisans belirtecini yapıştırın.",
  "license.install": "Lisansı yükle",
  "license.installed": "Lisans yüklendi.",
  "license.uploadFailed": "Yükleme başarısız oldu",
};

const MESSAGES: Record<Lang, Record<MessageKey, string>> = { en, tr };

interface LangState {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const LangContext = createContext<LangState | null>(null);

function storedLang(): Lang {
  // Turkey-first product (exporters selling into the EU): default to Turkish and
  // only honor a persisted explicit "en" choice. The toggle still switches freely.
  // /en is the English home page: start in English there, so the first render
  // matches the prerendered English markup instead of flashing Turkish.
  try {
    if (window.location.pathname === "/en") return "en";
    return localStorage.getItem(LANG_STORAGE_KEY) === "en" ? "en" : "tr";
  } catch {
    return "tr";
  }
}

/** Wraps @app/ui's LocaleProvider so one state drives formatting AND strings. */
export function LangProvider({
  children,
  initialLang,
}: {
  children: ReactNode;
  initialLang?: Lang | undefined;
}) {
  // initialLang: the build-time prerender (src/prerender.tsx) has no storage or URL.
  const [lang, setLangState] = useState<Lang>(() => initialLang ?? storedLang());

  const value = useMemo<LangState>(
    () => ({
      lang,
      setLang: (next) => {
        try {
          localStorage.setItem(LANG_STORAGE_KEY, next);
        } catch {
          /* storage unavailable — keep in-memory only */
        }
        setLangState(next);
      },
    }),
    [lang],
  );

  return (
    <LangContext.Provider value={value}>
      <LocaleProvider locale={LOCALE_BY_LANG[lang]} currency="USD">
        {children}
      </LocaleProvider>
    </LangContext.Provider>
  );
}

export function useLang(): LangState {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within <LangProvider>");
  return ctx;
}

/** Interpolation params for a message: `{min}`/`{max}`/… placeholders. */
export type TParams = Record<string, string | number>;

/** Replace `{name}` placeholders in a template; leaves unknown placeholders intact. */
export function interpolate(template: string, params?: TParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m));
}

/**
 * Translation lookup for the active language. Optional `params` fill `{name}`
 * placeholders (e.g. `t("field.rule.tooShort", { min: 2 })`). Backward
 * compatible — existing single-arg callers are unaffected.
 */
export function useT(): (key: MessageKey, params?: TParams) => string {
  const { lang } = useLang();
  return useMemo(
    () => (key: MessageKey, params?: TParams) => interpolate(MESSAGES[lang][key], params),
    [lang],
  );
}

/** Pick a schema-def localized label (labels carry tr/en/de) for the panel language. */
export function pickLabel(labels: { tr: string; en: string }, lang: Lang): string {
  return labels[lang];
}

/** Locale-aware date-time for table cells; "—" for missing values. */
export function useFormatDateTime(): (iso: string | null | undefined) => string {
  const { locale } = useLocale();
  return useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" });
    return (iso: string | null | undefined) => (iso ? fmt.format(new Date(iso)) : "—");
  }, [locale]);
}
